import React, { useState, useEffect, useCallback, useMemo, memo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useMsal } from '@azure/msal-react';
import { List } from 'react-window';
import { authenticatedApiCall } from '../../utils/apiClient';
import { useToast } from '../../hooks/useToast';
import { useConfirm } from '../../hooks/useConfirm';
import ConfirmDialog from '../common/ConfirmDialog';
import ContractorDetailModal from './ContractorDetailModal';
import ContractorAuditModal from './ContractorAuditModal';
import { usePresence, ActiveViewers } from '../common/ViewingTemplate';
import { COUNTRIES, getProvincesForCountry } from '../../config/contractorConstants';
import { ListenButton } from '../common/AudioGuide';
import HelpWithVideoTour from '../Help/HelpWithVideoTour';

/**
 * Filter Icon Component - SVG funnel icon matching DisciplineMatrix style
 */
const FilterIcon = memo(({ isActive, color = 'white' }) => (
  <svg 
    width="12" 
    height="12" 
    viewBox="0 0 16 16" 
    fill={isActive ? '#007bff' : color}
    style={{ display: 'block' }}
  >
    <path d="M1.5 1.5A.5.5 0 0 1 2 1h12a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-.128.334L10 8.692V13.5a.5.5 0 0 1-.342.474l-3 1A.5.5 0 0 1 6 14.5V8.692L1.628 3.834A.5.5 0 0 1 1.5 3.5v-2z"/>
  </svg>
));

/**
 * Format phone number as (xxx)-xxx-xxxx
 * Handles input progressively as user types
 */
const formatPhoneNumber = (value) => {
  if (!value) return '';
  // Strip all non-digits
  const digits = value.replace(/\D/g, '');
  // Limit to 10 digits (standard North American)
  const limited = digits.slice(0, 10);
  // Format progressively as user types
  if (limited.length <= 3) return limited;
  if (limited.length <= 6) return `(${limited.slice(0, 3)})-${limited.slice(3)}`;
  return `(${limited.slice(0, 3)})-${limited.slice(3, 6)}-${limited.slice(6)}`;
};

/**
 * Score input component - defined outside to be stable
 */
const ScoreInput = memo(({ value, onChange, disabled }) => (
  <select
    value={value || ''}
    onChange={(e) => onChange(e.target.value ? parseInt(e.target.value) : '')}
    disabled={disabled}
    style={{
      width: '60px',
      padding: '0.25rem',
      border: '1px solid #ddd',
      borderRadius: '4px',
      fontSize: '0.85rem',
      textAlign: 'center'
    }}
  >
    <option value="">-</option>
    <option value="1">1</option>
    <option value="2">2</option>
    <option value="3">3</option>
    <option value="4">4</option>
    <option value="5">5</option>
  </select>
));

/**
 * Score badge component - defined outside to be stable
 */
const ScoreBadge = memo(({ score }) => {
  if (!score) return (
    <span style={{ 
      backgroundColor: '#e9ecef',
      color: '#6c757d',
      padding: '1px 6px',
      borderRadius: '4px',
      fontWeight: 'bold',
      fontSize: '0.8rem'
    }}>-</span>
  );
  
  const getColor = (s) => {
    if (s >= 4) return { bg: '#d4edda', color: '#155724' };
    if (s >= 3) return { bg: '#fff3cd', color: '#856404' };
    return { bg: '#f8d7da', color: '#721c24' };
  };
  
  const colors = getColor(score);
  return (
    <span style={{
      backgroundColor: colors.bg,
      color: colors.color,
      padding: '1px 6px',
      borderRadius: '4px',
      fontWeight: 'bold',
      fontSize: '0.8rem'
    }}>
      {score}
    </span>
  );
});

/**
 * Overall score badge - defined outside to be stable
 * Now clickable to show score history
 */
const OverallScoreBadge = memo(({ score, onClick }) => {
  if (!score && score !== 0) return (
    <span style={{ 
      backgroundColor: '#e9ecef',
      color: '#6c757d',
      padding: '1px 6px',
      borderRadius: '4px',
      fontWeight: 'bold',
      fontSize: '0.8rem'
    }}>-</span>
  );
  
  const getColor = (s) => {
    if (s >= 4) return { bg: '#28a745', color: 'white' };
    if (s >= 3) return { bg: '#ffc107', color: '#212529' };
    if (s >= 2) return { bg: '#fd7e14', color: 'white' };
    return { bg: '#dc3545', color: 'white' };
  };
  
  const colors = getColor(score);
  return (
    <span 
      onClick={onClick}
      style={{
        backgroundColor: colors.bg,
        color: colors.color,
        padding: '1px 8px',
        borderRadius: '4px',
        fontWeight: 'bold',
        fontSize: '0.8rem',
        display: 'inline-block',
        minWidth: '40px',
        textAlign: 'center',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 0.1s ease',
      }}
      title={onClick ? 'Click to view score history' : undefined}
      onMouseEnter={(e) => onClick && (e.target.style.transform = 'scale(1.05)')}
      onMouseLeave={(e) => onClick && (e.target.style.transform = 'scale(1)')}
    >
      {parseFloat(score).toFixed(2)}
    </span>
  );
});

/**
 * Win/Loss Badge - displays wins and losses in a compact format
 */
const WinLossBadge = memo(({ wins, losses }) => {
  const hasData = wins > 0 || losses > 0;
  
  if (!hasData) {
    return (
      <span style={{ 
        backgroundColor: '#e9ecef',
        color: '#6c757d',
        padding: '1px 6px',
        borderRadius: '4px',
        fontWeight: 'bold',
        fontSize: '0.8rem'
      }}>-</span>
    );
  }
  
  return (
    <div style={{ 
      display: 'flex', 
      gap: '4px', 
      alignItems: 'center',
      fontSize: '0.8rem'
    }}>
      <span style={{ 
        color: '#155724',
        fontWeight: 'bold',
        backgroundColor: '#d4edda',
        padding: '2px 5px',
        borderRadius: '3px'
      }} title={`${wins} Win${wins !== 1 ? 's' : ''}`}>
        {wins}W
      </span>
      <span style={{ 
        color: '#721c24',
        fontWeight: 'bold',
        backgroundColor: '#f8d7da',
        padding: '2px 5px',
        borderRadius: '3px'
      }} title={`${losses} Loss${losses !== 1 ? 'es' : ''}`}>
        {losses}L
      </span>
    </div>
  );
});

/**
 * Insurance Status Badge - shows insurance status with color coding
 * White/gray = not set, Green = valid, Red = expired
 */
const InsuranceStatusBadge = memo(({ status }) => {
  const getStatusStyle = () => {
    switch (status) {
      case 'valid':
        return { 
          bg: '#d4edda', 
          color: '#155724', 
          icon: '✓',
          title: 'Insurance valid'
        };
      case 'expired':
        return { 
          bg: '#f8d7da', 
          color: '#721c24', 
          icon: '⚠',
          title: 'Insurance expired!'
        };
      default:
        return { 
          bg: '#e9ecef', 
          color: '#6c757d', 
          icon: '-',
          title: 'No insurance dates set'
        };
    }
  };
  
  const style = getStatusStyle();
  
  return (
    <span 
      style={{
        backgroundColor: style.bg,
        color: style.color,
        padding: '3px 8px',
        borderRadius: '4px',
        fontWeight: 'bold',
        fontSize: '0.85rem',
        display: 'inline-block'
      }}
      title={style.title}
    >
      {style.icon}
    </span>
  );
});

/**
 * Score History Modal - Shows historical overall scores over time
 */
const ScoreHistoryModal = memo(({ isOpen, onClose, contractorId, contractorName, instance, accounts }) => {
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [weights, setWeights] = useState(null);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    if (isOpen && contractorId) {
      fetchHistory();
    }
  }, [isOpen, contractorId]);
  
  const fetchHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await authenticatedApiCall(instance, accounts, 'GET', `/contractors/${contractorId}/score-history`);
      if (response.data.success) {
        setHistory(response.data.data || []);
        setWeights(response.data.current_weights);
      }
    } catch (err) {
      console.error('Error fetching score history:', err);
      setError(err.response?.data?.message || 'Failed to fetch score history');
    } finally {
      setLoading(false);
    }
  };
  
  if (!isOpen) return null;
  
  const getScoreColor = (score) => {
    if (!score && score !== 0) return '#999';
    if (score >= 4) return '#28a745';
    if (score >= 3) return '#ffc107';
    if (score >= 2) return '#fd7e14';
    return '#dc3545';
  };
  
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1050
      }}
      onClick={onClose}
    >
      <div 
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          width: '90%',
          maxWidth: '700px',
          maxHeight: '80vh',
          overflow: 'hidden',
          boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '1rem 1.5rem',
          borderBottom: '1px solid #dee2e6',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#17a2b8',
          color: 'white'
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.1rem' }}>📊 Score History</h3>
            <small style={{ opacity: 0.9 }}>{contractorName}</small>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              color: 'white',
              fontSize: '1.5rem',
              cursor: 'pointer',
              borderRadius: '50%',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ×
          </button>
        </div>
        
        {/* Content */}
        <div style={{ padding: '1.5rem', maxHeight: 'calc(80vh - 120px)', overflowY: 'auto' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>
              Loading score history...
            </div>
          ) : error ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#dc3545' }}>
              {error}
            </div>
          ) : history.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>
              No evaluation history available for this contractor.
            </div>
          ) : (
            <>
              {/* Current Weights Info */}
              {weights && (
                <div style={{
                  backgroundColor: '#f8f9fa',
                  padding: '0.75rem 1rem',
                  borderRadius: '8px',
                  marginBottom: '1rem',
                  fontSize: '0.85rem',
                  color: '#6c757d'
                }}>
                  <strong>Current Weights:</strong> Quality {weights.quality_weight}% | HSE {weights.hse_weight}% | Performance {weights.performance_weight}% | Commercial {weights.commercial_weight}% | Technical {weights.technical_weight}%
                </div>
              )}
              
              {/* History Table */}
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                <thead>
                  <tr style={{ backgroundColor: '#e9ecef' }}>
                    <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Date</th>
                    <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>⭐ Qual</th>
                    <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>🏗️ HSE</th>
                    <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>📈 Perf</th>
                    <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>💼 Comm</th>
                    <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>📐 Tech</th>
                    <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '2px solid #dee2e6', backgroundColor: '#d4edda' }}>Overall</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Evaluator</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((entry, idx) => (
                    <tr 
                      key={entry.evaluation_id} 
                      style={{ 
                        backgroundColor: idx === 0 ? '#fff3cd' : (idx % 2 === 0 ? '#fff' : '#f8f9fa'),
                        borderLeft: idx === 0 ? '3px solid #ffc107' : 'none'
                      }}
                    >
                      <td style={{ padding: '0.6rem 0.75rem', borderBottom: '1px solid #dee2e6' }}>
                        <div style={{ fontWeight: idx === 0 ? 'bold' : 'normal' }}>
                          {formatDate(entry.date)}
                        </div>
                        {idx === 0 && <small style={{ color: '#856404' }}>Most Recent</small>}
                      </td>
                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'center', borderBottom: '1px solid #dee2e6' }}>
                        {entry.quality ?? '-'}
                        {entry.avg_quality && entry.quality && (
                          <div style={{ fontSize: '0.7rem', color: '#6c757d' }}>avg: {entry.avg_quality}</div>
                        )}
                      </td>
                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'center', borderBottom: '1px solid #dee2e6' }}>
                        {entry.hse ?? '-'}
                        {entry.avg_hse && entry.hse && (
                          <div style={{ fontSize: '0.7rem', color: '#6c757d' }}>avg: {entry.avg_hse}</div>
                        )}
                      </td>
                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'center', borderBottom: '1px solid #dee2e6' }}>
                        {entry.performance ?? '-'}
                        {entry.avg_performance && entry.performance && (
                          <div style={{ fontSize: '0.7rem', color: '#6c757d' }}>avg: {entry.avg_performance}</div>
                        )}
                      </td>
                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'center', borderBottom: '1px solid #dee2e6' }}>
                        {entry.commercial ?? '-'}
                        {entry.avg_commercial && entry.commercial && (
                          <div style={{ fontSize: '0.7rem', color: '#6c757d' }}>avg: {entry.avg_commercial}</div>
                        )}
                      </td>
                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'center', borderBottom: '1px solid #dee2e6' }}>
                        {entry.technical ?? '-'}
                        {entry.avg_technical && entry.technical && (
                          <div style={{ fontSize: '0.7rem', color: '#6c757d' }}>avg: {entry.avg_technical}</div>
                        )}
                      </td>
                      <td style={{ 
                        padding: '0.6rem 0.75rem', 
                        textAlign: 'center', 
                        borderBottom: '1px solid #dee2e6',
                        backgroundColor: idx === 0 ? '#d4edda' : 'rgba(212, 237, 218, 0.3)'
                      }}>
                        <span style={{
                          backgroundColor: getScoreColor(entry.overall_score),
                          color: entry.overall_score >= 3 && entry.overall_score < 4 ? '#212529' : 'white',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          fontWeight: 'bold',
                          display: 'inline-block'
                        }}>
                          {entry.overall_score?.toFixed(2) ?? '-'}
                        </span>
                      </td>
                      <td style={{ padding: '0.6rem 0.75rem', borderBottom: '1px solid #dee2e6', fontSize: '0.85rem' }}>
                        {entry.evaluator || 'Unknown'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              <div style={{ marginTop: '1rem', fontSize: '0.8rem', color: '#6c757d', fontStyle: 'italic' }}>
                The overall score is a weighted average of all evaluations up to that point in time.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
});

/**
 * Yes/No badge component - for boolean fields like insurance and orientation
 */
const YesNoBadge = memo(({ value }) => {
  if (value === null || value === undefined) return (
    <span style={{ 
      backgroundColor: '#e9ecef',
      color: '#6c757d',
      padding: '1px 6px',
      borderRadius: '4px',
      fontWeight: 'bold',
      fontSize: '0.8rem'
    }}>-</span>
  );
  
  const isYes = Boolean(value);
  return (
    <span style={{
      backgroundColor: isYes ? '#d4edda' : '#f8d7da',
      color: isYes ? '#155724' : '#721c24',
      padding: '1px 6px',
      borderRadius: '4px',
      fontWeight: 'bold',
      fontSize: '0.8rem'
    }}>
      {isYes ? 'Yes' : 'No'}
    </span>
  );
});

/**
 * Yes/No checkbox input - for editing boolean fields
 */
const YesNoCheckbox = memo(({ value, onChange, disabled }) => (
  <label style={{ 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'center',
    gap: '0.25rem',
    cursor: disabled ? 'not-allowed' : 'pointer',
    padding: '0.25rem 0.5rem',
    backgroundColor: value ? '#d4edda' : '#f8d7da',
    borderRadius: '4px',
    border: '1px solid #ddd',
    minWidth: '55px'
  }}>
    <input
      type="checkbox"
      checked={Boolean(value)}
      onChange={(e) => onChange(e.target.checked)}
      disabled={disabled}
      style={{ width: '14px', height: '14px', cursor: disabled ? 'not-allowed' : 'pointer' }}
    />
    <span style={{ fontSize: '0.75rem', fontWeight: 'bold' }}>
      {value ? 'Yes' : 'No'}
    </span>
  </label>
));

/**
 * Get file type icon based on mime type or extension
 */
const getFileIcon = (fileType, filename) => {
  const ext = filename?.split('.').pop()?.toLowerCase();
  if (fileType?.includes('pdf') || ext === 'pdf') return { icon: '📄', color: '#dc3545' };
  if (fileType?.includes('word') || ext === 'doc' || ext === 'docx') return { icon: '📝', color: '#2b579a' };
  if (fileType?.includes('excel') || fileType?.includes('spreadsheet') || ext === 'xls' || ext === 'xlsx') return { icon: '📊', color: '#217346' };
  return { icon: '📎', color: '#6c757d' };
};

/**
 * Format file size for display
 */
const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

/**
 * Virtualized Row Component for react-window v2.x
 * Props come from rowProps, plus index and style from List
 */
const DEFAULT_COLUMN_WIDTHS = {
  checkbox: 50,
  id: 60,
  country: 130,
  state: 180,
  discipline: 160,
  contractor: 280,
  contact_name: 170,
  contact_phone: 160,
  contact_email: 260,
  quality: 110,
  hse: 85,
  performance: 145,
  commercial: 140,
  technical: 130,
  insurance: 120,
  orientation: 140,
  winloss: 100,
  overall: 100,
  updated_by: 150,
  updated_at: 160,
  discovery_comments: 250,
  discovery_job_number: 120,
  discovery_year: 80,
  last_bid: 150,
  last_project: 240,
  last_year: 90,
  source_file_url: 200,
};

const MIN_WIDTHS = {
  checkbox: 35,
  id: 40,
  country: 70,
  state: 100,
  discipline: 80,
  contractor: 120,
  contact_name: 70,
  contact_phone: 100,
  contact_email: 120,
  quality: 50,
  hse: 45,
  performance: 50,
  commercial: 50,
  technical: 50,
  insurance: 50,
  orientation: 50,
  winloss: 60,
  overall: 60,
  updated_by: 90,
  updated_at: 100,
  discovery_comments: 120,
  discovery_job_number: 70,
  discovery_year: 50,
  last_bid: 80,
  last_project: 100,
  last_year: 50,
  source_file_url: 90,
};

function hasReachableContact(contractor) {
  const nonempty = (v) => {
    const s = String(v || '').trim();
    return !!s && s !== '-';
  };
  if (nonempty(contractor?.contact_email) || nonempty(contractor?.contact_phone)) return true;
  if (Array.isArray(contractor?.contacts)) {
    return contractor.contacts.some((ct) => nonempty(ct.email) || nonempty(ct.phone));
  }
  return false;
}

// Frozen columns row component - renders only the frozen columns
const FrozenContractorRow = memo(({ 
  index, 
  style, 
  contractors, 
  selectedRows, 
  onSelectRow,
  onContractorClick,
  columnWidths,
  visibleColumns,
  canDelete = false
}) => {
  const contractor = contractors[index];
  if (!contractor) return null;
  
  const isSelected = selectedRows.has(contractor.id);
  const rowBgColor = index % 2 === 0 ? '#fff' : '#f8f9fa';
  const widthValues = columnWidths || DEFAULT_COLUMN_WIDTHS;
  
  const cellStyle = (colKey, extra = {}) => ({
    width: widthValues[colKey],
    minWidth: MIN_WIDTHS[colKey],
    flexShrink: 0,
    padding: '2px 6px',
    borderRight: '1px solid #dee2e6',
    display: 'flex',
    alignItems: 'center',
    boxSizing: 'border-box',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: '0.8rem',
    backgroundColor: rowBgColor,
    ...extra
  });
  
  return (
    <div style={{ ...style, display: 'flex', backgroundColor: rowBgColor, borderBottom: '1px solid #dee2e6' }}>
      {canDelete && (
        <div style={cellStyle('checkbox', { justifyContent: 'center' })}>
          <input type="checkbox" checked={isSelected} onChange={() => onSelectRow(contractor.id)} style={{ cursor: 'pointer' }} />
        </div>
      )}
      {visibleColumns?.id !== false && (
        <div style={cellStyle('id', { justifyContent: 'center', color: '#6c757d', fontSize: '0.8rem' })}>{contractor.id}</div>
      )}
      <div style={cellStyle('country')}>{contractor.country || '-'}</div>
      <div style={cellStyle('state')} title={(contractor.provinces || []).join(', ') || contractor.state || '-'}>
        {(contractor.provinces || []).length > 0
          ? contractor.provinces.join(', ')
          : contractor.state || '-'
        }
      </div>
      <div style={cellStyle('discipline')}>{contractor.discipline || '-'}</div>
      <div 
        style={cellStyle('contractor', { fontWeight: '500', color: '#007bff', cursor: 'pointer', textDecoration: 'underline', boxShadow: '2px 0 5px rgba(0,0,0,0.1)' })}
        title="Click to view/edit details"
        onClick={() => onContractorClick && onContractorClick(contractor)}
      >
        {contractor.contractor || '-'}
      </div>
    </div>
  );
});

// Scrollable columns row component - renders all columns except the frozen ones
const ScrollableContractorRow = memo(({ 
  index, 
  style, 
  contractors, 
  editingId, 
  editForm, 
  setEditForm,
  onEditSave,
  onEditCancel,
  onContractorClick,
  onScoreClick,
  saving,
  columnWidths,
  disciplines,
  canDelete = false,
  collapsedGroups = { scores: true, status: true },
  visibleColumns
}) => {
  const contractor = contractors[index];
  if (!contractor) return null;
  
  const isEditing = editingId === contractor.id;
  const rowBgColor = index % 2 === 0 ? '#fff' : '#f8f9fa';
  const widthValues = columnWidths || DEFAULT_COLUMN_WIDTHS;
  
  const scoresCollapsed = collapsedGroups?.scores !== false;
  const statusCollapsed = collapsedGroups?.status !== false;
  
  const cellStyle = (colKey, extra = {}) => ({
    width: widthValues[colKey],
    minWidth: MIN_WIDTHS[colKey],
    flexShrink: 0,
    padding: isEditing ? '1px 4px' : '2px 6px',
    borderRight: '1px solid #dee2e6',
    display: 'flex',
    alignItems: 'center',
    boxSizing: 'border-box',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: '0.8rem',
    backgroundColor: rowBgColor,
    ...extra
  });
  
  // Group collapse button spacer style (matches header button width)
  const groupSpacerStyle = {
    width: 18,
    minWidth: 18,
    maxWidth: 18,
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#e3f2fd',
    borderRight: '1px solid #dee2e6',
    boxSizing: 'border-box'
  };
  
  return (
    <div style={{ ...style, display: 'flex', backgroundColor: rowBgColor, borderBottom: '1px solid #dee2e6' }}>
      {/* Contact columns */}
      <div style={cellStyle('contact_name')}>{contractor.contact_name || '-'}</div>
      <div style={cellStyle('contact_phone')}>{contractor.contact_phone || '-'}</div>
      <div style={cellStyle('contact_email')}>{contractor.contact_email || '-'}</div>
      {visibleColumns?.updated_by && (
        <div style={cellStyle('updated_by')}>{contractor.updated_by || '-'}</div>
      )}
      {visibleColumns?.updated_at && (
        <div style={cellStyle('updated_at', { fontSize: '0.8rem' })}>
          {contractor.updated_at ? new Date(contractor.updated_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
        </div>
      )}
      {visibleColumns?.discovery_comments && (
        <div style={cellStyle('discovery_comments', { fontSize: '0.8rem' })}>{contractor.discovery_comments || '-'}</div>
      )}
      {visibleColumns?.discovery_job_number && (
        <div style={cellStyle('discovery_job_number')}>{contractor.discovery_job_number || '-'}</div>
      )}
      {visibleColumns?.discovery_year && (
        <div style={cellStyle('discovery_year', { justifyContent: 'center' })}>{contractor.discovery_year || '-'}</div>
      )}
      {visibleColumns?.last_bid && (
        <div style={cellStyle('last_bid')} title={contractor.last_bid || ''}>{contractor.last_bid || '-'}</div>
      )}
      {visibleColumns?.last_project && (
        <div style={cellStyle('last_project')} title={contractor.last_project || ''}>{contractor.last_project || '-'}</div>
      )}
      {visibleColumns?.last_year && (
        <div style={cellStyle('last_year', { justifyContent: 'center' })}>{contractor.last_year || '-'}</div>
      )}
      {visibleColumns?.source_file_url && (
        <div style={cellStyle('source_file_url')}>
          {contractor.source_file_url ? (
            <a href={contractor.source_file_url} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2', fontSize: '0.8rem', textDecoration: 'underline' }}>
              View File
            </a>
          ) : '-'}
        </div>
      )}
      
      {/* Scores group collapse button spacer */}
      <div style={groupSpacerStyle} />
      
      {/* Score columns - only show when expanded */}
      {!scoresCollapsed && (
        <>
          <div style={cellStyle('quality', { justifyContent: 'center', cursor: 'pointer' })} 
               onClick={() => onScoreClick && onScoreClick(contractor)}>
            <ScoreBadge score={contractor.quality} />
          </div>
          <div style={cellStyle('hse', { justifyContent: 'center' })}>
            <ScoreBadge score={contractor.hse} />
          </div>
          <div style={cellStyle('performance', { justifyContent: 'center' })}>
            <ScoreBadge score={contractor.performance} />
          </div>
          <div style={cellStyle('commercial', { justifyContent: 'center' })}>
            <ScoreBadge score={contractor.commercial} />
          </div>
          <div style={cellStyle('technical', { justifyContent: 'center' })}>
            <ScoreBadge score={contractor.technical} />
          </div>
        </>
      )}
      
      {/* Status group collapse button spacer */}
      <div style={groupSpacerStyle} />
      
      {/* Status columns - only show when expanded */}
      {!statusCollapsed && (
        <>
          <div style={cellStyle('insurance', { justifyContent: 'center' })}>
            <InsuranceStatusBadge status={contractor.insurance_status} />
          </div>
          <div style={cellStyle('orientation', { justifyContent: 'center' })}>
            <YesNoBadge value={contractor.orientation} />
          </div>
          <div style={cellStyle('winloss', { justifyContent: 'center' })}>
            <WinLossBadge wins={contractor.wins} losses={contractor.losses} />
          </div>
        </>
      )}
      
      <div style={cellStyle('overall', { justifyContent: 'center', backgroundColor: '#e3f7fc' })}>
        <OverallScoreBadge score={contractor.overall_point} onClick={() => onScoreClick && onScoreClick(contractor)} />
      </div>
    </div>
  );
});

const ContractorRow = memo(({ 
  index, 
  style, 
  contractors, 
  editingId, 
  editForm, 
  setEditForm,
  selectedRows, 
  onSelectRow, 
  onEditSave,
  onEditCancel,
  onContractorClick,
  onScoreClick,
  saving,
  columnWidths,
  disciplines,
  canDelete = false
}) => {
  const contractor = contractors[index];
  if (!contractor) return null;
  
  const isEditing = editingId === contractor.id;
  const isSelected = selectedRows.has(contractor.id);
  const rowBgColor = index % 2 === 0 ? '#fff' : '#f8f9fa';
  
  const frozenColumns = [
    ...(canDelete ? ['checkbox'] : []),
    'country', 'state', 'discipline', 'contractor'
  ];
  
  const widthValues = columnWidths || DEFAULT_COLUMN_WIDTHS;
  const getFrozenLeft = (colKey) => {
    const colIndex = frozenColumns.indexOf(colKey);
    if (colIndex === -1) return null;
    let left = 0;
    for (let i = 0; i < colIndex; i++) {
      left += widthValues[frozenColumns[i]] || MIN_WIDTHS[frozenColumns[i]];
    }
    return left;
  };
  
  // Cell style using fixed pixel widths (uses dynamic columnWidths from parent, falls back to default)
  const cellStyle = (colKey, extra = {}) => {
    const isFrozen = frozenColumns.includes(colKey);
    const frozenLeft = getFrozenLeft(colKey);
    const isLastFrozen = frozenColumns.indexOf(colKey) === frozenColumns.length - 1;
    
    return {
      width: widthValues[colKey],
      minWidth: MIN_WIDTHS[colKey],
      flexShrink: 0,
      padding: isEditing ? '1px 4px' : '2px 6px',
      borderRight: '1px solid #dee2e6',
      display: 'flex',
      alignItems: 'center',
      boxSizing: 'border-box',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      fontSize: '0.8rem',
      // Frozen column styles
      position: isFrozen ? 'sticky' : 'relative',
      left: isFrozen ? frozenLeft : undefined,
      zIndex: isFrozen ? 5 : 1,
      backgroundColor: isFrozen ? rowBgColor : undefined, // Ensure frozen cells have solid background
      // Add shadow on the last frozen column
      boxShadow: isLastFrozen ? '2px 0 5px rgba(0,0,0,0.1)' : undefined,
      ...extra
    };
  };
  
  if (isEditing) {
    // Edit mode row
    return (
      <div 
        style={{
          ...style,
          display: 'flex',
          alignItems: 'stretch',
          backgroundColor: rowBgColor,
          borderBottom: '1px solid #dee2e6',
        }}
      >
        {canDelete && (
          <div data-col-id="checkbox" style={cellStyle('checkbox', { justifyContent: 'center' })}>
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => onSelectRow(contractor.id)}
              style={{ cursor: 'pointer' }}
            />
          </div>
        )}
        <div data-col-id="id" style={cellStyle('id', { justifyContent: 'center', color: '#6c757d', fontSize: '0.8rem' })}>{contractor.id}</div>
        <div data-col-id="country" style={cellStyle('country')}>
          <select
            value={editForm.country || ''}
            onChange={(e) => setEditForm({ ...editForm, country: e.target.value })}
            style={{ width: '100%', padding: '0.25rem', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.8rem' }}
          >
            <option value="">Select</option>
            {COUNTRIES.map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
        <div data-col-id="state" style={cellStyle('state', { fontSize: '0.75rem', color: '#666' })} title="Edit provinces in detail modal">
          {(editForm.provinces || []).length > 0 
            ? (
                <>
                  {editForm.provinces.slice(0, 2).join(', ')}
                  {editForm.provinces.length > 2 && (
                    <span style={{ color: '#007bff', fontWeight: '600' }}>
                      {' '}+{editForm.provinces.length - 2}
                    </span>
                  )}
                </>
              )
            : editForm.state || '-'
          }
        </div>
        <div data-col-id="discipline" style={cellStyle('discipline')}>
          <select
            value={editForm.discipline || ''}
            onChange={(e) => setEditForm({ ...editForm, discipline: e.target.value })}
            style={{ width: '100%', padding: '0.25rem', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.8rem' }}
          >
            <option value="">Select</option>
            {(disciplines || []).map(d => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </div>
        <div data-col-id="contractor" style={cellStyle('contractor')}>
          <input
            type="text"
            value={editForm.contractor || ''}
            onChange={(e) => setEditForm({ ...editForm, contractor: e.target.value })}
            style={{ width: '100%', padding: '0.25rem', border: '1px solid #ddd', borderRadius: '4px' }}
          />
        </div>
        <div data-col-id="contact_name" style={cellStyle('contact_name')}>
          <input
            type="text"
            value={editForm.contact_name || ''}
            onChange={(e) => setEditForm({ ...editForm, contact_name: e.target.value })}
            style={{ width: '100%', padding: '0.25rem', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.8rem' }}
            placeholder="Name"
          />
        </div>
        <div data-col-id="contact_phone" style={cellStyle('contact_phone')}>
          <input
            type="text"
            value={editForm.contact_phone || ''}
            onChange={(e) => setEditForm({ ...editForm, contact_phone: formatPhoneNumber(e.target.value) })}
            style={{ width: '100%', padding: '0.25rem', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.8rem' }}
            placeholder="(xxx)-xxx-xxxx"
          />
        </div>
        <div data-col-id="contact_email" style={cellStyle('contact_email')}>
          <input
            type="text"
            value={editForm.contact_email || ''}
            onChange={(e) => setEditForm({ ...editForm, contact_email: e.target.value })}
            style={{ width: '100%', padding: '0.25rem', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.8rem' }}
            placeholder="Email"
          />
        </div>
        <div data-col-id="quality" style={cellStyle('quality', { justifyContent: 'center' })}>
          <ScoreInput value={editForm.quality} onChange={(v) => setEditForm({ ...editForm, quality: v })} />
        </div>
        <div data-col-id="hse" style={cellStyle('hse', { justifyContent: 'center' })}>
          <ScoreInput value={editForm.hse} onChange={(v) => setEditForm({ ...editForm, hse: v })} />
        </div>
        <div data-col-id="performance" style={cellStyle('performance', { justifyContent: 'center' })}>
          <ScoreInput value={editForm.performance} onChange={(v) => setEditForm({ ...editForm, performance: v })} />
        </div>
        <div data-col-id="commercial" style={cellStyle('commercial', { justifyContent: 'center' })}>
          <ScoreInput value={editForm.commercial} onChange={(v) => setEditForm({ ...editForm, commercial: v })} />
        </div>
        <div data-col-id="technical" style={cellStyle('technical', { justifyContent: 'center' })}>
          <ScoreInput value={editForm.technical} onChange={(v) => setEditForm({ ...editForm, technical: v })} />
        </div>
        <div data-col-id="insurance" style={cellStyle('insurance', { justifyContent: 'center' })} title="Edit in contractor detail modal">
          <InsuranceStatusBadge status={contractor.insurance_status} />
        </div>
        <div data-col-id="orientation" style={cellStyle('orientation', { justifyContent: 'center' })}>
          <YesNoCheckbox value={editForm.orientation} onChange={(v) => setEditForm({ ...editForm, orientation: v })} />
        </div>
        <div data-col-id="winloss" style={cellStyle('winloss', { justifyContent: 'center' })}>
          <WinLossBadge wins={contractor.wins} losses={contractor.losses} />
        </div>
        <div data-col-id="overall" style={cellStyle('overall', { justifyContent: 'center', backgroundColor: '#e3f7fc' })}>
          <span style={{ color: '#999', fontSize: '0.8rem' }}>Auto</span>
        </div>
      </div>
    );
  }
  
  // View mode row
  return (
    <div 
      style={{
        ...style,
        display: 'flex',
        alignItems: 'stretch',
        backgroundColor: rowBgColor,
        borderBottom: '1px solid #dee2e6',
      }}
    >
      {canDelete && (
        <div data-col-id="checkbox" style={cellStyle('checkbox', { justifyContent: 'center' })}>
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onSelectRow(contractor.id)}
            style={{ cursor: 'pointer' }}
          />
        </div>
      )}
      <div data-col-id="id" style={cellStyle('id', { justifyContent: 'center', color: '#6c757d', fontSize: '0.8rem' })}>{contractor.id}</div>
      <div data-col-id="country" style={cellStyle('country')} title={contractor.country}>{contractor.country}</div>
      <div data-col-id="state" style={cellStyle('state')} title={(contractor.provinces || []).join(', ') || contractor.state || '-'}>
        {(contractor.provinces || []).length > 0
          ? contractor.provinces.join(', ')
          : contractor.state || '-'
        }
      </div>
      <div data-col-id="discipline" style={cellStyle('discipline')} title={contractor.discipline}>{contractor.discipline}</div>
      <div 
        data-col-id="contractor"
        style={cellStyle('contractor', { 
          fontWeight: '500', 
          cursor: 'pointer',
          color: '#007bff',
          textDecoration: 'underline'
        })} 
        title="Click to view/edit details"
        onClick={() => onContractorClick(contractor)}
      >
        {contractor.contractor}
      </div>
      <div data-col-id="contact_name" style={cellStyle('contact_name', { fontSize: '0.85rem' })} title={contractor.contact_name || '-'}>{contractor.contact_name || '-'}</div>
      <div data-col-id="contact_phone" style={cellStyle('contact_phone', { fontSize: '0.85rem' })} title={contractor.contact_phone || '-'}>{contractor.contact_phone ? formatPhoneNumber(contractor.contact_phone) : '-'}</div>
      <div data-col-id="contact_email" style={cellStyle('contact_email', { fontSize: '0.85rem' })} title={contractor.contact_email || '-'}>{contractor.contact_email || '-'}</div>
      <div data-col-id="quality" style={cellStyle('quality', { justifyContent: 'center' })}><ScoreBadge score={contractor.quality} /></div>
      <div data-col-id="hse" style={cellStyle('hse', { justifyContent: 'center' })}><ScoreBadge score={contractor.hse} /></div>
      <div data-col-id="performance" style={cellStyle('performance', { justifyContent: 'center' })}><ScoreBadge score={contractor.performance} /></div>
      <div data-col-id="commercial" style={cellStyle('commercial', { justifyContent: 'center' })} title="Commercial Documentation"><ScoreBadge score={contractor.commercial} /></div>
      <div data-col-id="technical" style={cellStyle('technical', { justifyContent: 'center' })} title="Technical Documentation"><ScoreBadge score={contractor.technical} /></div>
      <div data-col-id="insurance" style={cellStyle('insurance', { justifyContent: 'center' })}><InsuranceStatusBadge status={contractor.insurance_status} /></div>
      <div data-col-id="orientation" style={cellStyle('orientation', { justifyContent: 'center' })}><YesNoBadge value={contractor.orientation} /></div>
      <div data-col-id="winloss" style={cellStyle('winloss', { justifyContent: 'center' })} title="Win/Loss Record"><WinLossBadge wins={contractor.wins} losses={contractor.losses} /></div>
      <div data-col-id="overall" style={{ ...cellStyle('overall', { justifyContent: 'center', backgroundColor: '#e3f7fc' }), borderRight: 'none' }}>
        <OverallScoreBadge score={contractor.overall_point} onClick={() => onScoreClick(contractor)} />
      </div>
    </div>
  );
});

/**
 * VirtualizedContractorsTable - Table component with virtualized rows
 * Uses react-window for efficient rendering of large datasets
 */
const VirtualizedContractorsTable = memo(({
  filteredContractors,
  selectedRows,
  editingId,
  editForm,
  setEditForm,
  saving,
  sortConfig,
  columnFilters,
  filterMode,
  filterText,
  openFilterColumn,
  filterSearch,
  handleSort,
  handleSelectAll,
  handleSelectRow,
  handleEditStart,
  handleEditSave,
  handleEditCancel,
  handleContractorClick,
  handleScoreClick,
  handleFilterToggle,
  selectAllColumnFilter,
  clearColumnFilter,
  applyTextFilter,
  getUniqueValues,
  setOpenFilterColumn,
  setFilterSearch,
  disciplines, // Receive disciplines list from parent
  canDelete = false, // Permission to delete - controls checkbox visibility
  columnWidthsFromParent, // Parent-controlled column widths for undo capability
  setColumnWidthsFromParent, // Setter for parent-controlled column widths
  saveColumnWidthsToHistory, // Save to history before resize
  collapsedGroups = { scores: true, status: true },
  toggleGroupCollapsed,
  visibleColumns
}) => {
  // Column group definitions (for reference, not used in render logic)
  // scoreGroupCols: ['quality', 'hse', 'performance', 'commercial', 'technical']
  // statusGroupCols: ['insurance', 'orientation', 'winloss']
  
  // Row height for virtualization
  const rowHeight = 32;
  const tableHeight = filteredContractors.length * rowHeight;
  
  // Table container ref
  const tableContainerRef = useRef(null);
  
  // Refs for synced scrolling between frozen list and scrollable list
  // Using listRef for imperative API (scrollTo)
  const frozenListRef = useRef(null);
  const scrollableListRef = useRef(null);
  const scrollbarRef = useRef(null);
  const currentScrollOffset = useRef(0);
  
  // Check if scrollbar is needed (content height > table height)
  const needsScrollbar = filteredContractors.length * rowHeight > tableHeight;
  const totalContentHeight = filteredContractors.length * rowHeight;
  const maxScrollOffset = Math.max(0, totalContentHeight - tableHeight);
  
  // Scroll both Lists to a specific offset
  const scrollBothLists = useCallback((offset, skipScrollbar = false) => {
    const newOffset = Math.max(0, Math.min(maxScrollOffset, offset));
    currentScrollOffset.current = newOffset;
    
    // Safety check: ensure ref exists AND has scrollTo method before calling
    if (frozenListRef.current && typeof frozenListRef.current.scrollTo === 'function') {
      frozenListRef.current.scrollTo(newOffset);
    }
    if (scrollableListRef.current && typeof scrollableListRef.current.scrollTo === 'function') {
      scrollableListRef.current.scrollTo(newOffset);
    }
    // Also sync the visible scrollbar (when not initiated by scrollbar itself)
    if (!skipScrollbar && scrollbarRef.current) {
      scrollbarRef.current.scrollTop = newOffset;
    }
  }, [maxScrollOffset]);
  
  // Handle wheel events to sync both Lists
  const handleWheelScroll = useCallback((e) => {
    if (!needsScrollbar) return;
    e.preventDefault();
    
    // Calculate new scroll position
    const delta = e.deltaY;
    const newOffset = currentScrollOffset.current + delta;
    scrollBothLists(newOffset);
  }, [needsScrollbar, scrollBothLists]);
  
  // Handle scrollbar drag/click
  const handleScrollbarScroll = useCallback((e) => {
    const scrollbar = scrollbarRef.current;
    if (!scrollbar) return;
    
    const newOffset = scrollbar.scrollTop;
    scrollBothLists(newOffset, true); // Skip syncing back to scrollbar
  }, [scrollBothLists]);
  
  // Use parent-controlled column widths (with undo capability)
  const columnWidths = columnWidthsFromParent;
  const setColumnWidths = setColumnWidthsFromParent;
  
  // Resizing state
  const [resizing, setResizing] = useState(null);
  const resizeStartX = useRef(0);
  const resizeStartFlex = useRef({});
  
  const minWidths = useMemo(() => ({ ...MIN_WIDTHS }), []);
  
  const frozenColumns = [
    ...(canDelete ? ['checkbox'] : []),
    ...(visibleColumns?.id !== false ? ['id'] : []),
    'country', 'state', 'discipline', 'contractor'
  ];
  
  // Calculate cumulative left positions for frozen columns
  const getFrozenLeft = (colKey) => {
    const colIndex = frozenColumns.indexOf(colKey);
    if (colIndex === -1) return null;
    let left = 0;
    for (let i = 0; i < colIndex; i++) {
      left += columnWidths[frozenColumns[i]] || minWidths[frozenColumns[i]];
    }
    return left;
  };
  
  // Score columns get light blue background, others get grayish
  const scoreColumns = ['quality', 'hse', 'performance', 'commercial', 'technical', 'insurance', 'orientation', 'winloss', 'overall'];
  
  // Header cell style - fixed width sizing (allows horizontal scroll)
  // New vertical layout: column name on top, filter button + icon below
  const headerCellStyle = (colKey, extra = {}) => {
    const isFrozen = frozenColumns.includes(colKey);
    const frozenLeft = getFrozenLeft(colKey);
    const isLastFrozen = frozenColumns.indexOf(colKey) === frozenColumns.length - 1;
    const isScoreColumn = scoreColumns.includes(colKey);
    
    // Light gray for data columns, light blue for score columns
    const bgColor = isScoreColumn ? '#e3f2fd' : '#e9ecef';
    const textColor = isScoreColumn ? '#1565c0' : '#495057';
    const borderColor = isScoreColumn ? '#bbdefb' : '#dee2e6';
    
    return {
      width: columnWidths[colKey],
      minWidth: minWidths[colKey],
      flexShrink: 0,
      padding: '0.5rem',
      fontWeight: '600',
      borderRight: `1px solid ${borderColor}`,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'flex-start',
      boxSizing: 'border-box',
      position: isFrozen ? 'sticky' : 'relative',
      left: isFrozen ? frozenLeft : undefined,
      zIndex: isFrozen ? 10 : 1,
      backgroundColor: bgColor,
      color: textColor,
      userSelect: 'none',
      overflow: 'hidden',
      gap: '4px',
      // Add shadow on the last frozen column to show separation
      boxShadow: isLastFrozen ? '2px 0 5px rgba(0,0,0,0.15)' : undefined,
      ...extra
    };
  };
  
  // Header content component for consistent layout
  const HeaderContent = ({ columnKey, label, icon, hasFilter = true, showTextFilters = false }) => {
    // Determine if this is a score column (blue background) to use matching filter icon color
    const isScoreCol = scoreColumns.includes(columnKey);
    const filterIconColor = isScoreCol ? '#1565c0' : '#495057'; // Dark blue for blue bg, dark gray for gray bg
    
    return (
      <>
        {/* Column name on top */}
        <span 
          onClick={() => handleSort(columnKey)} 
          style={{ 
            cursor: 'pointer', 
            fontSize: '0.75rem', 
            textAlign: 'center',
            lineHeight: 1.2,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '100%'
          }} 
          title={`${label} - Click to sort`}
        >
          {label}
          {sortConfig.key === columnKey && (
            <span style={{ marginLeft: '2px' }}>{sortConfig.direction === 'asc' ? '▲' : '▼'}</span>
          )}
        </span>
        {/* Filter button and icon below */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'center' }}>
          {icon && <span style={{ fontSize: '0.9rem' }}>{icon}</span>}
          {hasFilter && (
            <span 
              onClick={(e) => openFilterAtPosition(e, columnKey)} 
              style={{ 
                cursor: 'pointer', 
                opacity: (columnFilters[columnKey]?.length > 0 || filterMode[columnKey]) ? 1 : 0.7, 
                display: 'flex', 
                alignItems: 'center',
                padding: '2px'
              }} 
              title="Filter"
            >
              <FilterIcon 
                isActive={columnFilters[columnKey]?.length > 0 || filterMode[columnKey]} 
                color={filterIconColor}
              />
            </span>
          )}
        </div>
      </>
    );
  };
  
  // Column resize handlers - pixel-based, only affects current column
  const handleResizeStart = useCallback((e, colKey, nextColKey) => {
    e.preventDefault();
    e.stopPropagation();
    // Save current state to history before starting resize
    if (saveColumnWidthsToHistory) saveColumnWidthsToHistory();
    setResizing({ colKey, nextColKey });
    resizeStartX.current = e.clientX;
    resizeStartFlex.current = { [colKey]: columnWidths[colKey] };
  }, [columnWidths, saveColumnWidthsToHistory]);
  
  const handleResizeMove = useCallback((e) => {
    if (!resizing) return;
    
    const { colKey } = resizing;
    const deltaX = e.clientX - resizeStartX.current;
    const newWidth = Math.max(minWidths[colKey], resizeStartFlex.current[colKey] + deltaX);
    
    setColumnWidths(prev => ({
      ...prev,
      [colKey]: newWidth
    }));
  }, [resizing, minWidths]);
  
  const handleResizeEnd = useCallback(() => {
    setResizing(null);
  }, []);
  
  // Add/remove resize event listeners
  useEffect(() => {
    if (resizing) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }
    return () => {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [resizing, handleResizeMove, handleResizeEnd]);
  
  // Resize handle component
  const ResizeHandle = ({ colKey, nextColKey }) => (
    <div
      onMouseDown={(e) => handleResizeStart(e, colKey, nextColKey)}
      style={{
        position: 'absolute',
        right: 0,
        top: 0,
        bottom: 0,
        width: '6px',
        cursor: 'col-resize',
        backgroundColor: resizing?.colKey === colKey ? 'rgba(255,255,255,0.3)' : 'transparent',
        zIndex: 10
      }}
      onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.2)'}
      onMouseLeave={(e) => e.target.style.backgroundColor = resizing?.colKey === colKey ? 'rgba(255,255,255,0.3)' : 'transparent'}
    />
  );
  
  // Wrap handleScoreClick to ensure stable reference (may be undefined from props initially)
  const stableScoreClick = useCallback((contractor) => {
    if (handleScoreClick) handleScoreClick(contractor);
  }, [handleScoreClick]);
  
  // Memoize row props for List
  const rowProps = useMemo(() => ({
    contractors: filteredContractors,
    editingId,
    editForm,
    setEditForm,
    selectedRows,
    onSelectRow: handleSelectRow,
    onEditSave: handleEditSave,
    onEditCancel: handleEditCancel,
    onContractorClick: handleContractorClick,
    onScoreClick: stableScoreClick,
    saving,
    columnWidths,
    disciplines,
    canDelete
  }), [filteredContractors, editingId, editForm, setEditForm, selectedRows, handleSelectRow, handleEditSave, handleEditCancel, handleContractorClick, stableScoreClick, saving, columnWidths, disciplines, canDelete]);

  // Store filter button positions for portal rendering
  const [filterButtonPositions, setFilterButtonPositions] = useState({});
  
  // Filter dropdown component with portal rendering for proper positioning
  const FilterDropdown = ({ columnKey, showTextFilters = false }) => {
    const dropdownRef = useRef(null);
    const isNumericColumn = ['quality', 'hse', 'performance', 'commercial', 'technical', 'overall_point'].includes(columnKey);
    const [activeTab, setActiveTab] = useState('values');
    const [numericOperator, setNumericOperator] = useState('gte');
    const [numericValue, setNumericValue] = useState('');
    const [textOperator, setTextOperator] = useState('contains');

    const textOperators = [
      { value: 'contains', label: 'Contains' },
      { value: 'notContains', label: 'Does not contain' },
      { value: 'equals', label: 'Equals' },
      { value: 'notEquals', label: 'Does not equal' },
      { value: 'startsWith', label: 'Starts with' },
      { value: 'endsWith', label: 'Ends with' },
      { value: 'empty', label: 'Is empty' },
      { value: 'notEmpty', label: 'Is not empty' },
    ];

    const numericOperators = [
      { value: 'eq', label: 'Equals (=)' },
      { value: 'neq', label: 'Not equals (≠)' },
      { value: 'gt', label: 'Greater than (>)' },
      { value: 'gte', label: 'Greater or equal (≥)' },
      { value: 'lt', label: 'Less than (<)' },
      { value: 'lte', label: 'Less or equal (≤)' },
    ];

    useEffect(() => {
      const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
          setOpenFilterColumn(null);
          setFilterSearch('');
        }
      };
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }, []);

    useEffect(() => {
      const handleClickOutside = (e) => {
        if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
          setOpenFilterColumn(null);
          setFilterSearch('');
        }
      };
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 100);
      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, []);

    const handleApplyAdvancedFilter = () => {
      if (isNumericColumn) {
        if (numericValue !== '') {
          applyTextFilter(columnKey, numericOperator, numericValue);
        }
      } else {
        if (['empty', 'notEmpty'].includes(textOperator)) {
          applyTextFilter(columnKey, textOperator, '');
        } else if (filterSearch.trim()) {
          applyTextFilter(columnKey, textOperator, filterSearch);
        }
      }
    };

    const tabStyle = (isActive) => ({
      flex: 1,
      padding: '8px 10px',
      fontSize: '11px',
      fontWeight: '600',
      backgroundColor: isActive ? 'white' : '#f1f3f4',
      border: 'none',
      borderBottom: isActive ? '2px solid #007bff' : '2px solid transparent',
      cursor: 'pointer',
      color: isActive ? '#007bff' : '#5f6368',
    });

    // Get position from stored button positions
    const pos = filterButtonPositions[columnKey] || { top: 100, left: 100 };

    // Use portal to render dropdown at document body level
    return ReactDOM.createPortal(
      <div 
        ref={dropdownRef} 
        className="filter-dropdown-portal" 
        style={{ 
          position: 'fixed', 
          top: pos.top, 
          left: Math.min(pos.left, window.innerWidth - 320), // Prevent overflow right
          backgroundColor: 'white', 
          border: '1px solid #dee2e6', 
          borderRadius: '6px', 
          boxShadow: '0 4px 20px rgba(0,0,0,0.2)', 
          zIndex: 99999, 
          width: '300px', 
          maxHeight: '480px', 
          overflow: 'hidden', 
          display: 'flex', 
          flexDirection: 'column', 
          color: '#333' 
        }} 
        onClick={e => e.stopPropagation()}
      >
        <div style={{ padding: '10px 12px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f8f9fa' }}>
          <span style={{ fontSize: '13px', fontWeight: '600' }}>Filter: {columnKey}</span>
          <button onClick={() => { setOpenFilterColumn(null); setFilterSearch(''); }} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#666', padding: '0 4px', lineHeight: 1 }}>×</button>
        </div>

        <div style={{ display: 'flex', borderBottom: '1px solid #eee' }}>
          <button onClick={() => setActiveTab('values')} style={tabStyle(activeTab === 'values')}>Select Values</button>
          <button onClick={() => setActiveTab('advanced')} style={tabStyle(activeTab === 'advanced')}>
            Advanced
            {filterText[columnKey] && <span style={{ marginLeft: '4px', color: '#007bff' }}>●</span>}
          </button>
        </div>

        {activeTab === 'values' ? (
          <>
            <div style={{ padding: '10px', borderBottom: '1px solid #eee' }}>
              <input type="text" placeholder="Search values..." value={filterSearch} onChange={(e) => setFilterSearch(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (filterSearch.trim()) { applyTextFilter(columnKey, 'contains', filterSearch); } } }} style={{ width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '12px', boxSizing: 'border-box' }} autoFocus />
            </div>

            <div style={{ padding: '8px 10px', borderBottom: '1px solid #eee', display: 'flex', gap: '8px' }}>
              <button onClick={() => selectAllColumnFilter(columnKey)} style={{ padding: '5px 10px', fontSize: '11px', border: 'none', borderRadius: '4px', cursor: 'pointer', backgroundColor: '#28a745', color: 'white', fontWeight: '500' }}>✓ Select All</button>
              <button onClick={() => clearColumnFilter(columnKey)} style={{ padding: '5px 10px', fontSize: '11px', border: 'none', borderRadius: '4px', cursor: 'pointer', backgroundColor: '#dc3545', color: 'white', fontWeight: '500' }}>✕ Clear</button>
            </div>

            {(columnFilters[columnKey]?.length > 0 || filterText[columnKey]) && (
              <div style={{ padding: '6px 10px', backgroundColor: '#d1fae5', borderBottom: '1px solid #eee', fontSize: '11px', color: '#065f46' }}>
                <strong>Active:</strong> {columnFilters[columnKey]?.length > 0 ? `${columnFilters[columnKey].length} values` : ''} {filterText[columnKey] ? `${filterMode[columnKey]} "${filterText[columnKey]}"` : ''}
              </div>
            )}

            <div style={{ padding: '4px 0', maxHeight: '200px', overflow: 'auto', flex: '1 1 auto' }}>
              {getUniqueValues(columnKey).filter(val => !filterSearch || val.toLowerCase().includes(filterSearch.toLowerCase())).slice(0, 100).map(val => {
                // undefined = all selected; [] = Clear (none checked); else explicit list
                const selected = columnFilters[columnKey];
                const isChecked = selected === undefined ? true : (selected.length === 0 ? false : selected.includes(val));
                return (
                  <label key={val} style={{ display: 'flex', alignItems: 'center', padding: '6px 10px', cursor: 'pointer', fontSize: '12px', backgroundColor: isChecked ? '#e8f5e9' : 'transparent' }} onMouseEnter={(e) => { if (!isChecked) e.currentTarget.style.backgroundColor = '#f5f5f5'; }} onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isChecked ? '#e8f5e9' : 'transparent'; }}>
                    <input type="checkbox" checked={isChecked} onChange={() => handleFilterToggle(columnKey, val)} style={{ marginRight: '10px', accentColor: '#28a745' }} />
                    {val || '(empty)'}
                  </label>
                );
              })}
            </div>
          </>
        ) : (
          <div style={{ padding: '12px', flex: 1, overflow: 'auto' }}>
            {isNumericColumn ? (
              <>
                <div style={{ marginBottom: '10px' }}>
                  <label style={{ fontSize: '11px', color: '#666', display: 'block', marginBottom: '4px', fontWeight: '500' }}>Condition</label>
                  <select value={numericOperator} onChange={(e) => setNumericOperator(e.target.value)} style={{ width: '100%', padding: '8px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '4px' }}>
                    {numericOperators.map(op => <option key={op.value} value={op.value}>{op.label}</option>)}
                  </select>
                </div>
                <div style={{ marginBottom: '10px' }}>
                  <label style={{ fontSize: '11px', color: '#666', display: 'block', marginBottom: '4px', fontWeight: '500' }}>Value (0-5)</label>
                  <input type="number" min="0" max="5" step="0.1" value={numericValue} onChange={(e) => setNumericValue(e.target.value)} placeholder="Enter number..." style={{ width: '100%', padding: '8px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box' }} />
                </div>
              </>
            ) : (
              <>
                <div style={{ marginBottom: '10px' }}>
                  <label style={{ fontSize: '11px', color: '#666', display: 'block', marginBottom: '4px', fontWeight: '500' }}>Condition</label>
                  <select value={textOperator} onChange={(e) => setTextOperator(e.target.value)} style={{ width: '100%', padding: '8px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '4px' }}>
                    {textOperators.map(op => <option key={op.value} value={op.value}>{op.label}</option>)}
                  </select>
                </div>
                {!['empty', 'notEmpty'].includes(textOperator) && (
                  <div style={{ marginBottom: '10px' }}>
                    <label style={{ fontSize: '11px', color: '#666', display: 'block', marginBottom: '4px', fontWeight: '500' }}>Value</label>
                    <input type="text" value={filterSearch} onChange={(e) => setFilterSearch(e.target.value)} placeholder="Enter text..." style={{ width: '100%', padding: '8px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box' }} onKeyDown={(e) => { if (e.key === 'Enter') handleApplyAdvancedFilter(); }} />
                  </div>
                )}
              </>
            )}
            <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
              <button onClick={handleApplyAdvancedFilter} style={{ flex: 1, padding: '8px', fontSize: '12px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '500' }}>Apply</button>
              <button onClick={() => clearColumnFilter(columnKey)} style={{ flex: 1, padding: '8px', fontSize: '12px', backgroundColor: '#f1f3f4', color: '#333', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Clear</button>
            </div>

            {filterText[columnKey] && (
              <div style={{ marginTop: '12px', padding: '8px', backgroundColor: '#d1fae5', borderRadius: '4px', fontSize: '11px', color: '#065f46' }}>
                <strong>Active:</strong> {filterMode[columnKey]} "{filterText[columnKey]}"
              </div>
            )}
          </div>
        )}

        <div style={{ padding: '10px', borderTop: '1px solid #eee', display: 'flex', justifyContent: 'flex-end', backgroundColor: '#f8f9fa' }}>
          <button type="button" onClick={() => { if (filterSearch.trim()) { applyTextFilter(columnKey, 'contains', filterSearch); } setOpenFilterColumn(null); setFilterSearch(''); }} style={{ padding: '6px 16px', fontSize: '12px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '500' }}>Done</button>
        </div>
      </div>,
      document.body
    );
  };

  // Handler to open filter and store button position
  const openFilterAtPosition = (e, columnKey) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setFilterButtonPositions(prev => ({
      ...prev,
      [columnKey]: { top: rect.bottom + 5, left: rect.left }
    }));
    setFilterSearch('');
    setOpenFilterColumn(openFilterColumn === columnKey ? null : columnKey);
  };

  // Header height for calculations
  const headerHeight = 52;
  
  const frozenWidth = (canDelete ? columnWidths.checkbox : 0) + 
    (visibleColumns?.id !== false ? columnWidths.id : 0) + columnWidths.country + columnWidths.state + columnWidths.discipline + columnWidths.contractor;
  
  // Row props for frozen list
  const frozenRowProps = {
    contractors: filteredContractors,
    selectedRows,
    onSelectRow: handleSelectRow,
    onContractorClick: handleContractorClick,
    columnWidths,
    visibleColumns,
    canDelete
  };
  
  // Row props for scrollable list
  const scrollableRowProps = {
    contractors: filteredContractors,
    editingId,
    editForm,
    setEditForm,
    onEditSave: handleEditSave,
    onEditCancel: handleEditCancel,
    onContractorClick: handleContractorClick,
    onScoreClick: handleScoreClick,
    saving,
    columnWidths,
    disciplines,
    canDelete,
    collapsedGroups,
    visibleColumns
  };
  
  return (
    <div style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
      <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '550px' }}>
        <div style={{ display: 'flex', minWidth: 'max-content' }}>
      {/* FROZEN COLUMNS SECTION - Sticky left, high z-index to stay above scrollable headers */}
      <div style={{ 
        flexShrink: 0, 
        width: frozenWidth, 
        borderRight: '2px solid #dee2e6',
        boxShadow: '2px 0 5px rgba(0,0,0,0.1)',
        zIndex: 20,
        backgroundColor: 'white',
        position: 'sticky',
        left: 0
      }}>
        {/* Frozen Header - Sticky top, highest z-index */}
        <div style={{ display: 'flex', borderBottom: '2px solid #dee2e6', position: 'sticky', top: 0, zIndex: 25, backgroundColor: 'white' }}>
          {canDelete && (
            <div data-col-id="checkbox" style={headerCellStyle('checkbox', { justifyContent: 'center' })}>
              <input 
                type="checkbox"
                checked={selectedRows.size === filteredContractors.length && filteredContractors.length > 0}
                onChange={(e) => handleSelectAll(e.target.checked)}
                style={{ cursor: 'pointer' }}
              />
              <ResizeHandle colKey="checkbox" />
            </div>
          )}
          {visibleColumns?.id !== false && (
            <div data-col-id="id" style={headerCellStyle('id', { justifyContent: 'center' })}>
              <HeaderContent columnKey="id" label="ID" hasFilter={false} />
              <ResizeHandle colKey="id" />
            </div>
          )}
          <div data-col-id="country" style={headerCellStyle('country')}>
            <HeaderContent columnKey="country" label="Country" icon="🌍" hasFilter={true} />
            <ResizeHandle colKey="country" />
          </div>
          <div data-col-id="state" style={headerCellStyle('state')}>
            <HeaderContent columnKey="state" label="Province/State" icon="📍" hasFilter={true} />
            <ResizeHandle colKey="state" />
          </div>
          <div data-col-id="discipline" style={headerCellStyle('discipline')}>
            <HeaderContent columnKey="discipline" label="Discipline" icon="🔧" hasFilter={true} />
            <ResizeHandle colKey="discipline" />
          </div>
          <div data-col-id="contractor" style={headerCellStyle('contractor', { borderRight: 'none' })}>
            <HeaderContent columnKey="contractor" label="Contractor" icon="🏢" hasFilter={true} />
            <ResizeHandle colKey="contractor" />
          </div>
        </div>
        
        {/* Frozen Body */}
        {filteredContractors.length === 0 ? (
          <div style={{ height: tableHeight }} />
        ) : (
          <List
            listRef={frozenListRef}
            rowComponent={FrozenContractorRow}
            rowCount={filteredContractors.length}
            rowHeight={rowHeight}
            rowProps={frozenRowProps}
            overscanCount={10}
            style={{ height: tableHeight, width: '100%', overflow: 'hidden' }}
          />
        )}
      </div>
      
      {/* SCROLLABLE COLUMNS SECTION - Right side.
          flexShrink:0 (not flex:1) so the section is exactly as wide as its columns —
          otherwise it stretches to fill the card and renders blank trailing "columns"
          with the vertical scrollbar detached far to the right. */}
      <div ref={tableContainerRef} style={{ flexShrink: 0 }}>
        <div style={{ minWidth: 'max-content' }}>
          {/* Scrollable Header - Sticky top */}
          <div style={{ display: 'flex', borderBottom: '2px solid #dee2e6', position: 'sticky', top: 0, zIndex: 15, backgroundColor: 'white' }}>
            <div data-col-id="contact_name" style={headerCellStyle('contact_name')}>
              <HeaderContent columnKey="contact_name" label="Contact" icon="📞" hasFilter={true} />
              <ResizeHandle colKey="contact_name" />
            </div>
            <div data-col-id="contact_phone" style={headerCellStyle('contact_phone')}>
              <HeaderContent columnKey="contact_phone" label="Phone" icon="📱" hasFilter={true} />
              <ResizeHandle colKey="contact_phone" />
            </div>
            <div data-col-id="contact_email" style={headerCellStyle('contact_email')}>
              <HeaderContent columnKey="contact_email" label="Email" icon="✉️" hasFilter={true} />
              <ResizeHandle colKey="contact_email" />
            </div>
            {visibleColumns?.updated_by && (
              <div data-col-id="updated_by" style={headerCellStyle('updated_by')}>
                <HeaderContent columnKey="updated_by" label="Updated By" hasFilter={true} />
                <ResizeHandle colKey="updated_by" />
              </div>
            )}
            {visibleColumns?.updated_at && (
              <div data-col-id="updated_at" style={headerCellStyle('updated_at')}>
                <HeaderContent columnKey="updated_at" label="Updated At" hasFilter={true} />
                <ResizeHandle colKey="updated_at" />
              </div>
            )}
            {visibleColumns?.discovery_comments && (
              <div data-col-id="discovery_comments" style={headerCellStyle('discovery_comments')}>
                <HeaderContent columnKey="discovery_comments" label="Comments" hasFilter={true} />
                <ResizeHandle colKey="discovery_comments" />
              </div>
            )}
            {visibleColumns?.discovery_job_number && (
              <div data-col-id="discovery_job_number" style={headerCellStyle('discovery_job_number')}>
                <HeaderContent columnKey="discovery_job_number" label="Job #" hasFilter={true} />
                <ResizeHandle colKey="discovery_job_number" />
              </div>
            )}
            {visibleColumns?.discovery_year && (
              <div data-col-id="discovery_year" style={headerCellStyle('discovery_year')}>
                <HeaderContent columnKey="discovery_year" label="Year" hasFilter={true} />
                <ResizeHandle colKey="discovery_year" />
              </div>
            )}
            {visibleColumns?.last_bid && (
              <div data-col-id="last_bid" style={headerCellStyle('last_bid')}>
                <HeaderContent columnKey="last_bid" label="Last Bid" hasFilter={true} />
                <ResizeHandle colKey="last_bid" />
              </div>
            )}
            {visibleColumns?.last_project && (
              <div data-col-id="last_project" style={headerCellStyle('last_project')}>
                <HeaderContent columnKey="last_project" label="Last Project" hasFilter={true} />
                <ResizeHandle colKey="last_project" />
              </div>
            )}
            {visibleColumns?.last_year && (
              <div data-col-id="last_year" style={headerCellStyle('last_year')}>
                <HeaderContent columnKey="last_year" label="Last Year" hasFilter={true} />
                <ResizeHandle colKey="last_year" />
              </div>
            )}
            {visibleColumns?.source_file_url && (
              <div data-col-id="source_file_url" style={headerCellStyle('source_file_url')}>
                <HeaderContent columnKey="source_file_url" label="Source File" hasFilter={true} />
                <ResizeHandle colKey="source_file_url" />
              </div>
            )}
            
            {/* SCORES GROUP - Excel-like collapse button */}
            <div 
              data-col-id="scores_group_btn"
              onClick={(e) => { e.stopPropagation(); toggleGroupCollapsed('scores'); }}
              style={{
                width: 18,
                minWidth: 18,
                maxWidth: 18,
                height: '100%',
                padding: '2px 0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#e3f2fd',
                borderRight: collapsedGroups?.scores ? '1px solid #dee2e6' : 'none',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: 'bold',
                color: '#666',
                boxSizing: 'border-box',
              }}
              title={collapsedGroups?.scores ? 'Expand Scores' : 'Collapse Scores'}
            >
              {collapsedGroups?.scores ? '+' : '−'}
            </div>
            
            {/* Score Columns - only show when expanded */}
            {!collapsedGroups?.scores && (
              <>
                <div data-col-id="quality" style={headerCellStyle('quality')}>
                  <HeaderContent columnKey="quality" label="Quality" icon="⭐" hasFilter={true} />
                  <ResizeHandle colKey="quality" />
                </div>
                <div data-col-id="hse" style={headerCellStyle('hse')}>
                  <HeaderContent columnKey="hse" label="HSE" icon="🦺" hasFilter={true} />
                  <ResizeHandle colKey="hse" />
                </div>
                <div data-col-id="performance" style={headerCellStyle('performance')}>
                  <HeaderContent columnKey="performance" label="Performance" icon="📈" hasFilter={true} />
                  <ResizeHandle colKey="performance" />
                </div>
                <div data-col-id="commercial" style={headerCellStyle('commercial')}>
                  <HeaderContent columnKey="commercial" label="Commercial" icon="💼" hasFilter={true} />
                  <ResizeHandle colKey="commercial" />
                </div>
                <div data-col-id="technical" style={headerCellStyle('technical')}>
                  <HeaderContent columnKey="technical" label="Technical" icon="📐" hasFilter={true} />
                  <ResizeHandle colKey="technical" />
                </div>
              </>
            )}
            
            {/* STATUS GROUP - Excel-like collapse button */}
            <div 
              data-col-id="status_group_btn"
              onClick={(e) => { e.stopPropagation(); toggleGroupCollapsed('status'); }}
              style={{
                width: 18,
                minWidth: 18,
                maxWidth: 18,
                height: '100%',
                padding: '2px 0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#e3f2fd',
                borderRight: collapsedGroups?.status ? '1px solid #dee2e6' : 'none',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: 'bold',
                color: '#666',
                boxSizing: 'border-box',
              }}
              title={collapsedGroups?.status ? 'Expand Status' : 'Collapse Status'}
            >
              {collapsedGroups?.status ? '+' : '−'}
            </div>
            
            {/* Status Columns - only show when expanded */}
            {!collapsedGroups?.status && (
              <>
                <div data-col-id="insurance" style={headerCellStyle('insurance')}>
                  <HeaderContent columnKey="insurance" label="Insurance" icon="🛡️" hasFilter={true} />
                  <ResizeHandle colKey="insurance" />
                </div>
                <div data-col-id="orientation" style={headerCellStyle('orientation')}>
                  <HeaderContent columnKey="orientation" label="Orientation" icon="🧭" hasFilter={true} />
                  <ResizeHandle colKey="orientation" />
                </div>
                <div data-col-id="winloss" style={headerCellStyle('winloss')}>
                  <HeaderContent columnKey="winloss" label="Win/Loss" icon="🏆" hasFilter={false} />
                  <ResizeHandle colKey="winloss" />
                </div>
              </>
            )}
            
            <div data-col-id="overall" style={{ ...headerCellStyle('overall', { backgroundColor: '#17a2b8', color: 'white' }), borderRight: 'none' }}>
              <HeaderContent columnKey="overall_point" label="Score" icon="📊" hasFilter={true} />
              <ResizeHandle colKey="overall" />
            </div>
          </div>
          
          {/* Scrollable Body */}
          {filteredContractors.length === 0 ? (
            <div style={{ height: tableHeight }} />
          ) : (
            <List
              listRef={scrollableListRef}
              rowComponent={ScrollableContractorRow}
              rowCount={filteredContractors.length}
              rowHeight={rowHeight}
              rowProps={scrollableRowProps}
              overscanCount={10}
              style={{ height: tableHeight, width: '100%', overflow: 'hidden' }}
            />
          )}
        </div>
      </div>
      
        </div>
      </div>
      {/* Filter Dropdown Portal */}
      {openFilterColumn && <FilterDropdown columnKey={openFilterColumn} showTextFilters={true} />}
    </div>
  );
});

/**
 * SelectedContractors - Dashboard for managing contractors database
 * Similar to the progress tracking matrix view
 */
const SelectedContractors = ({ onOpenCriteriaModal, entityType = 'subcontractor' }) => {
  const { instance, accounts } = useMsal();

  // Which kind of record this screen is showing: 'subcontractor' or 'supplier'.
  // Driven by the route/menu but switchable in-page via a dropdown (like the
  // Mechanical progress-tracker type selector). The two share one table/codebase.
  const [viewType, setViewType] = useState(entityType === 'supplier' ? 'supplier' : 'subcontractor');
  useEffect(() => { setViewType(entityType === 'supplier' ? 'supplier' : 'subcontractor'); }, [entityType]);
  const isSupplier = viewType === 'supplier';
  const entityLabel = isSupplier ? 'Supplier' : 'Contractor';
  const entityLabelPlural = isSupplier ? 'Suppliers' : 'Contractors';

  // Live data push: ref + callback for real-time sync
  const fetchContractorsRef = useRef(null);
  const handleDataChange = useCallback(() => {
    if (fetchContractorsRef.current) fetchContractorsRef.current();
  }, []);

  // Real-time presence tracking
  const { viewers, isConnected, connectionState, socketId } = usePresence('selectedContractors', 'global', true, handleDataChange);

  // Socket ID tracking for sync headers
  const socketIdRef = useRef(null);
  useEffect(() => { socketIdRef.current = socketId; }, [socketId]);

  const syncHeaders = useCallback(() => {
    return socketIdRef.current ? { headers: { 'x-socket-id': socketIdRef.current } } : {};
  }, []);
  
  const initialAutofitDone = useRef(false);
  
  // State
  const [contractors, setContractors] = useState([]);
  const [weights, setWeights] = useState(null);
  const [disciplines, setDisciplines] = useState([]); // Fetched from API
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const { toast, showToast } = useToast();
  const { confirm, dialogProps } = useConfirm();
  
  // Filters
  const [filterCountry, setFilterCountry] = useState('');
  const [contactFilter, setContactFilter] = useState('has'); // has | missing | all
  const [filterDiscipline, setFilterDiscipline] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Edit state
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [newContractor, setNewContractor] = useState({
    country: '',
    provinces: [],
    discipline: '',
    contractor: '',
    insurance: false,
    quality: '',
    hse: '',
    orientation: false,
    performance: '',
    notes: '',
    contacts: [{ contact_name: '', phone: '', email: '', province_state: '' }] // Multiple contacts support
  });
  const [newContractorFiles, setNewContractorFiles] = useState([]);
  const newContractorFileInputRef = useRef(null);
  
  // Import/Export state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importing, setImporting] = useState(false);
  
  // Audit Log modal state
  const [showAuditModal, setShowAuditModal] = useState(false);
  
  // Help modal state
  const [showHelpModal, setShowHelpModal] = useState(false);
  
  // Advanced column filtering (like MaterialIndexLog)
  const [columnFilters, setColumnFilters] = useState({});
  const [openFilterColumn, setOpenFilterColumn] = useState(null);
  const [filterSearch, setFilterSearch] = useState('');
  const [filterMode, setFilterMode] = useState({});
  const [filterText, setFilterText] = useState({});
  
  // Row selection for batch delete
  const [selectedRows, setSelectedRows] = useState(new Set());
  
  // Detail modal state
  const [detailModalContractor, setDetailModalContractor] = useState(null);
  
  // Edit modal state
  const [editModalContractor, setEditModalContractor] = useState(null);
  const [editModalFiles, setEditModalFiles] = useState([]);
  const editModalFileInputRef = useRef(null);
  
  // Score history modal state
  const [scoreHistoryContractor, setScoreHistoryContractor] = useState(null);
  
  // Sorting
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

  // Collapsible column groups state - default to collapsed, persist in localStorage
  // Two groups: "scores" (quality, hse, performance, commercial, technical) and "status" (insurance, orientation, winloss)
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    try {
      const saved = localStorage.getItem('selectedContractors_collapsedGroups');
      // Default to both collapsed if no preference saved
      return saved ? JSON.parse(saved) : { scores: true, status: true };
    } catch {
      return { scores: true, status: true };
    }
  });
  
  // Toggle a specific group's collapsed state
  const toggleGroupCollapsed = useCallback((groupName) => {
    setCollapsedGroups(prev => {
      const newValue = { ...prev, [groupName]: !prev[groupName] };
      try {
        localStorage.setItem('selectedContractors_collapsedGroups', JSON.stringify(newValue));
      } catch (e) {
        console.warn('Failed to save collapsedGroups to localStorage:', e);
      }
      return newValue;
    });
  }, []);

  // Column visibility state
  const [showColumnConfig, setShowColumnConfig] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState({
    id: false,
    country: true,
    state: true,
    discipline: true,
    contractor: true,
    contact_name: true,
    contact_phone: true,
    contact_email: true,
    quality: true,
    hse: true,
    performance: true,
    commercial: true,
    technical: true,
    insurance: true,
    orientation: true,
    winloss: true,
    overall: true,
    updated_by: false,
    updated_at: false,
    discovery_comments: false,
    discovery_job_number: false,
    discovery_year: false,
    source_file_url: false,
    last_bid: false,
    last_project: false,
    last_year: false,
  });

  const defaultColumnWidthsParent = useMemo(() => ({
    checkbox: 50,
    id: 60,
    country: 130,
    state: 180,
    discipline: 160,
    contractor: 280,
    contact_name: 170,
    contact_phone: 160,
    contact_email: 260,
    quality: 110,
    hse: 85,
    performance: 145,
    commercial: 140,
    technical: 130,
    insurance: 120,
    orientation: 140,
    winloss: 100,
    overall: 100,
    updated_by: 150,
    updated_at: 160,
    discovery_comments: 250,
    discovery_job_number: 120,
    discovery_year: 80,
    source_file_url: 200,
    last_bid: 150,
    last_project: 240,
    last_year: 90,
  }), []);
  
  // Column widths state (parent-controlled for undo capability)
  const [columnWidthsState, setColumnWidthsState] = useState(defaultColumnWidthsParent);
  
  // Column width history for undo (stores previous states)
  const [columnWidthHistory, setColumnWidthHistory] = useState([]);
  const maxHistoryLength = 20;
  
  // Save current widths to history before any change
  const saveColumnWidthsToHistory = useCallback(() => {
    setColumnWidthHistory(prev => {
      const newHistory = [...prev, { ...columnWidthsState }];
      if (newHistory.length > maxHistoryLength) {
        return newHistory.slice(-maxHistoryLength);
      }
      return newHistory;
    });
  }, [columnWidthsState]);
  
  // Undo column width change
  const undoColumnResize = useCallback(() => {
    if (columnWidthHistory.length > 0) {
      const lastState = columnWidthHistory[columnWidthHistory.length - 1];
      setColumnWidthsState(lastState);
      setColumnWidthHistory(prev => prev.slice(0, -1));
    }
  }, [columnWidthHistory]);
  
  // Reset columns to default widths
  const resetColumnWidths = useCallback(() => {
    saveColumnWidthsToHistory();
    setColumnWidthsState(defaultColumnWidthsParent);
  }, [defaultColumnWidthsParent, saveColumnWidthsToHistory]);
  
  const autoFitColumns = useCallback(() => {
    saveColumnWidthsToHistory();
    const autoWidths = {
      checkbox: 50,
      id: 60,
      country: 130,
      state: 180,
      discipline: 160,
      contractor: 280,
      contact_name: 170,
      contact_phone: 160,
      contact_email: 260,
      quality: 110,
      hse: 85,
      performance: 145,
      commercial: 140,
      technical: 130,
      insurance: 120,
      orientation: 140,
      winloss: 100,
      overall: 100
    };
    setColumnWidthsState(autoWidths);
  }, [saveColumnWidthsToHistory]);
  
  useEffect(() => {
    if (contractors.length > 0 && !initialAutofitDone.current) {
      initialAutofitDone.current = true;
      autoFitColumns();
    }
  }, [contractors, autoFitColumns]);
  // Keyboard shortcut for undo (Ctrl+Z) - column resizing
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        if (!['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement?.tagName)) {
          e.preventDefault();
          undoColumnResize();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [undoColumnResize]);

  // Permissions state
  const [permissions, setPermissions] = useState({
    weights: false,
    edit: false,
    delete: false,
    export: false,
    import: false,
    isAdmin: false
  });

  // Fetch user permissions on mount
  const fetchPermissions = useCallback(async () => {
    try {
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'GET',
        '/auth/me'
      );
      
      if (response.data.success) {
        const user = response.data.user;
        const userPerms = user.authority_permissions || [];
        // Check for admin/superadmin via primary_role_id or hierarchy level
        const isAdmin = ['superadmin', 'admin'].includes(user.primary_role_id) || 
                        (user.role_hierarchy_level !== null && user.role_hierarchy_level <= 5);
        
        setPermissions({
          weights: isAdmin || userPerms.includes('contractor_weights'),
          edit: isAdmin || userPerms.includes('contractor_edit'),
          delete: isAdmin || userPerms.includes('contractor_delete'),
          export: isAdmin || userPerms.includes('contractor_export'),
          import: isAdmin || userPerms.includes('contractor_import'),
          isAdmin
        });
      }
    } catch (err) {
      console.error('Error fetching permissions:', err);
      // Default to no permissions on error
    }
  }, [instance, accounts]);

  // Fetch data - only refetch when dropdown filters change, not on search
  // Search filtering is done client-side from the loaded data
  const fetchContractors = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('type', viewType);
      if (filterCountry) params.append('country', filterCountry);
      if (filterDiscipline) params.append('discipline', filterDiscipline);
      // Note: searchTerm filtering is done client-side in filteredContractors
      
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'GET',
        `/contractors?${params.toString()}`
      );
      
      if (response.data.success) {
        setContractors(response.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching contractors:', err);
      setError('Failed to load contractors');
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, filterCountry, filterDiscipline, viewType]);

  useEffect(() => { fetchContractorsRef.current = fetchContractors; }, [fetchContractors]);

  const fetchWeights = useCallback(async () => {
    try {
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'GET',
        '/contractors/weights'
      );
      
      if (response.data.success) {
        setWeights(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching weights:', err);
    }
  }, [instance, accounts]);

  // Fetch disciplines from API (admin-managed list)
  const fetchDisciplines = useCallback(async () => {
    try {
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'GET',
        '/contractors/disciplines'
      );
      
      if (response.data.success) {
        // Extract just the names for the dropdown
        setDisciplines(response.data.data.map(d => d.name));
      }
    } catch (err) {
      console.error('Error fetching disciplines:', err);
      // Fallback to empty array - form will still work, just no options
      setDisciplines([]);
    }
  }, [instance, accounts]);

  useEffect(() => {
    fetchContractors();
    fetchWeights();
    fetchDisciplines();
    fetchPermissions();
  }, [fetchContractors, fetchWeights, fetchDisciplines, fetchPermissions]);


  // Export contractors to Excel
  const handleExport = async () => {
    try {
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'GET',
        `/contractors/export?type=${viewType}`,
        null,
        { responseType: 'blob' }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${entityLabelPlural.toLowerCase()}_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      showToast('Contractors exported successfully', 'success');
    } catch (err) {
      console.error('Error exporting contractors:', err);
      showToast('Failed to export contractors', 'error');
    }
  };

  // Import contractors from Excel
  const handleImport = async (file) => {
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      setImporting(true);
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'POST',
        `/contractors/import?type=${viewType}`,
        formData,
        { 
          headers: { 'Content-Type': 'multipart/form-data', ...syncHeaders().headers }
        }
      );
      
      if (response.data.success) {
        showToast(`Import successful: ${response.data.imported} ${entityLabelPlural.toLowerCase()} imported`, 'success');
        fetchContractors();
        setShowImportModal(false);
      }
    } catch (err) {
      console.error('Error importing contractors:', err);
      showToast(err.response?.data?.message || 'Failed to import contractors', 'error');
    } finally {
      setImporting(false);
    }
  };

  // Advanced filtering functions
  const getUniqueValues = useCallback((columnKey) => {
    const values = new Set();
    if (columnKey === 'state') {
      contractors.forEach(contractor => {
        const provs = contractor.provinces;
        if (Array.isArray(provs) && provs.length > 0) {
          provs.forEach(p => { if (p) values.add(String(p)); });
        } else if (contractor.state) {
          values.add(String(contractor.state));
        }
      });
    } else {
      contractors.forEach(contractor => {
        const val = contractor[columnKey];
        if (val !== null && val !== undefined && val !== '') {
          values.add(String(val));
        }
      });
    }
    return Array.from(values).sort();
  }, [contractors]);

  const handleSort = useCallback((columnKey) => {
    setSortConfig(prev => {
      if (prev.key === columnKey) {
        if (prev.direction === 'asc') return { key: columnKey, direction: 'desc' };
        if (prev.direction === 'desc') return { key: null, direction: null };
      }
      return { key: columnKey, direction: 'asc' };
    });
  }, []);

  const handleFilterToggle = useCallback((columnKey, value) => {
    setColumnFilters(prev => {
      const allValues = getUniqueValues(columnKey);
      const current = prev[columnKey];
      
      if (!current) {
        const newValues = allValues.filter(v => v !== value);
        return { ...prev, [columnKey]: newValues };
      }
      
      if (current.includes(value)) {
        const newValues = current.filter(v => v !== value);
        if (newValues.length === 0) { 
          const { [columnKey]: _, ...rest } = prev; 
          return rest; 
        }
        return { ...prev, [columnKey]: newValues };
      } else {
        const newValues = [...current, value];
        if (newValues.length >= allValues.length) {
          const { [columnKey]: _, ...rest } = prev;
          return rest;
        }
        return { ...prev, [columnKey]: newValues };
      }
    });
  }, [getUniqueValues]);

  const clearColumnFilter = useCallback((columnKey) => {
    setColumnFilters(prev => ({ ...prev, [columnKey]: [] })); // Empty array = no values selected (uncheck all)
    setFilterMode(prev => { const { [columnKey]: _, ...rest } = prev; return rest; });
    setFilterText(prev => { const { [columnKey]: _, ...rest } = prev; return rest; });
    // Don't close dropdown - let user continue working
  }, []);

  const selectAllColumnFilter = useCallback((columnKey) => {
    setColumnFilters(prev => { const { [columnKey]: _, ...rest } = prev; return rest; });
    setFilterMode(prev => { const { [columnKey]: _, ...rest } = prev; return rest; });
    setFilterText(prev => { const { [columnKey]: _, ...rest } = prev; return rest; });
  }, []);

  const clearAllFilters = useCallback(() => {
    setColumnFilters({});
    setFilterMode({});
    setFilterText({});
    setSearchTerm('');
    setFilterSearch('');
    setOpenFilterColumn(null);
    setFilterCountry('');
    setFilterDiscipline('');
    setContactFilter('all');
    showToast('All filters cleared', 'success');
  }, []);

  const applyTextFilter = useCallback((columnKey, mode, text) => {
    // For empty/notEmpty modes, text is not required
    const isEmptyMode = ['empty', 'notEmpty'].includes(mode);
    
    if (!text.trim() && !isEmptyMode) {
      setFilterMode(prev => { const { [columnKey]: _, ...rest } = prev; return rest; });
      setFilterText(prev => { const { [columnKey]: _, ...rest } = prev; return rest; });
      setColumnFilters(prev => { const { [columnKey]: _, ...rest } = prev; return rest; });
    } else {
      setFilterMode(prev => ({ ...prev, [columnKey]: mode }));
      setFilterText(prev => ({ ...prev, [columnKey]: isEmptyMode ? '' : text }));
      setColumnFilters(prev => { const { [columnKey]: _, ...rest } = prev; return rest; });
    }
    setOpenFilterColumn(null);
    setFilterSearch('');
  }, []);

  const getActiveFilters = useCallback(() => {
    const active = [];
    Object.entries(columnFilters).forEach(([key, values]) => {
      if (values && values.length > 0) {
        active.push({ key, type: 'values', count: values.length });
      }
    });
    Object.entries(filterMode).forEach(([key, mode]) => {
      if (mode && filterText[key]) {
        active.push({ key, type: 'text', mode, value: filterText[key] });
      }
    });
    return active;
  }, [columnFilters, filterMode, filterText]);

  const activeFilters = getActiveFilters();
  const hasActiveFilters = activeFilters.length > 0 || searchTerm || filterCountry || filterDiscipline || contactFilter !== 'all';

  // CRUD operations
  const handleAddContractor = async () => {
    if (!newContractor.country || !newContractor.discipline || !newContractor.contractor) {
      showToast('Country, Discipline, and Contractor name are required', 'error');
      return;
    }

    try {
      setSaving(true);
      
      // Filter out empty contacts
      const validContacts = (newContractor.contacts || []).filter(
        c => c.contact_name || c.phone || c.email
      );
      
      const contractorData = {
        ...newContractor,
        entity_type: viewType,
        contacts: validContacts
      };
      
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'POST',
        '/contractors',
        contractorData,
        syncHeaders()
      );
      
      if (response.data.success) {
        const newContractorId = response.data.data.id;
        
        // Upload any pending files
        if (newContractorFiles.length > 0) {
          for (const file of newContractorFiles) {
            const formData = new FormData();
            formData.append('file', file);
            try {
              await authenticatedApiCall(instance, accounts, 'POST', `/contractors/${newContractorId}/attachments`, formData, {
                headers: { 'Content-Type': 'multipart/form-data', ...syncHeaders().headers }
              });
            } catch (uploadErr) {
              console.error('Error uploading file:', uploadErr);
            }
          }
        }
        
        showToast('Contractor added successfully! Add another or click Done.');
        // Keep form open but clear it for next entry
        setNewContractor({
          country: newContractor.country, // Keep country for convenience
          state: newContractor.state,     // Keep state for convenience
          discipline: newContractor.discipline, // Keep discipline for convenience
          contractor: '',
          insurance: '',
          quality: '',
          hse: '',
          orientation: '',
          performance: '',
          notes: '',
          contacts: [{ contact_name: '', phone: '', email: '' }]
        });
        setNewContractorFiles([]);
        fetchContractors();
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to add contractor', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleEditStart = (contractor) => {
    setEditingId(contractor.id);
    setEditForm({ ...contractor });
  };

  // Save contractor from edit modal
  const handleEditModalSave = async () => {
    if (!editModalContractor) return;
    if (!editModalContractor.country || !editModalContractor.discipline || !editModalContractor.contractor) {
      showToast('Country, Discipline, and Contractor name are required', 'error');
      return;
    }
    try {
      setSaving(true);
      const validContacts = (editModalContractor.contacts || []).filter(
        c => c.contact_name || c.phone || c.email
      );
      const contractorData = {
        ...editModalContractor,
        contacts: validContacts
      };
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'PUT',
        `/contractors/${editModalContractor.id}`,
        contractorData,
        syncHeaders()
      );
      if (response.data.success) {
        if (editModalFiles.length > 0) {
          for (const file of editModalFiles) {
            const formData = new FormData();
            formData.append('file', file);
            try {
              await authenticatedApiCall(instance, accounts, 'POST', `/contractors/${editModalContractor.id}/attachments`, formData, {
                headers: { 'Content-Type': 'multipart/form-data', ...syncHeaders().headers }
              });
            } catch (fileErr) {
              console.warn('Failed to upload file:', file.name, fileErr);
            }
          }
        }
        showToast('Contractor updated successfully', 'success');
        setEditModalContractor(null);
        setEditModalFiles([]);
        setSelectedRows(new Set());
        fetchContractors();
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to update contractor', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Open contractor detail modal
  const handleContractorClick = useCallback((contractor) => {
    setDetailModalContractor(contractor);
  }, []);

  // Open score history modal
  const handleScoreClick = useCallback((contractor) => {
    setScoreHistoryContractor(contractor);
  }, []);

  // Save contractor from detail modal
  const handleDetailModalSave = useCallback(async (updatedContractor) => {
    try {
      setSaving(true);
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'PUT',
        `/contractors/${updatedContractor.id}`,
        updatedContractor,
        syncHeaders()
      );
      
      if (response.data.success) {
        showToast('Contractor updated successfully', 'success');
        setDetailModalContractor(null);
        fetchContractors();
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to update contractor', 'error');
    } finally {
      setSaving(false);
    }
  }, [instance, accounts, showToast, fetchContractors, syncHeaders]);

  const handleEditSave = async () => {
    try {
      setSaving(true);
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'PUT',
        `/contractors/${editingId}`,
        editForm,
        syncHeaders()
      );
      
      if (response.data.success) {
        showToast('Contractor updated successfully');
        setEditingId(null);
        setEditForm({});
        fetchContractors();
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to update contractor', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Delete handlers
  const handleDeleteRow = useCallback(async (id) => {
    const confirmed = await confirm({
      title: 'Delete Contractor',
      message: 'Are you sure you want to delete this contractor?',
      confirmText: 'Delete',
      type: 'danger',
    });
    if (!confirmed) return;
    try {
      const response = await authenticatedApiCall(instance, accounts, 'DELETE', `/contractors/${id}`, null, syncHeaders());
      if (response.data.success) {
        setContractors(prev => prev.filter(c => c.id !== id));
        showToast('Contractor deleted', 'success');
      }
    } catch (err) {
      console.error('Error deleting contractor:', err);
      showToast('Delete failed', 'error');
    }
  }, [instance, accounts, confirm, showToast]);

  const handleBulkDelete = async () => {
    if (selectedRows.size === 0) return;
    const confirmed = await confirm({
      title: 'Delete Contractors',
      message: `Are you sure you want to delete ${selectedRows.size} contractor(s)?`,
      confirmText: 'Delete All',
      type: 'danger',
    });
    if (!confirmed) return;
    
    try {
      const response = await authenticatedApiCall(instance, accounts, 'POST', '/contractors/delete-batch', {
        ids: Array.from(selectedRows)
      }, syncHeaders());
      if (response.data.success) {
        setContractors(prev => prev.filter(c => !selectedRows.has(c.id)));
        setSelectedRows(new Set());
        showToast(`${response.data.deletedCount} contractors deleted`, 'success');
      }
    } catch (err) {
      console.error('Error deleting contractors:', err);
      showToast('Bulk delete failed', 'error');
    }
  };

  const handleSelectRow = (id) => {
    setSelectedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) newSet.delete(id);
      else newSet.add(id);
      return newSet;
    });
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedRows(new Set(filteredContractors.map(c => c.id)));
    } else {
      setSelectedRows(new Set());
    }
  };

  // Filtered contractors with advanced filtering
  const filteredContractors = React.useMemo(() => {
    let result = [...contractors];
    
    // Apply search term
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      result = result.filter(c =>
        c.contractor?.toLowerCase().includes(search) ||
        c.country?.toLowerCase().includes(search) ||
        (Array.isArray(c.provinces) && c.provinces.some(p => p?.toLowerCase().includes(search))) ||
        c.state?.toLowerCase().includes(search) ||
        c.discipline?.toLowerCase().includes(search) ||
        c.contact_name?.toLowerCase().includes(search) ||
        c.contact_phone?.toLowerCase().includes(search) ||
        c.contact_email?.toLowerCase().includes(search)
      );
    }
    
    // Apply dropdown filters
    if (filterCountry) {
      result = result.filter(c => c.country === filterCountry);
    }
    if (filterDiscipline) {
      result = result.filter(c => c.discipline === filterDiscipline);
    }
    if (contactFilter === 'has') {
      result = result.filter(hasReachableContact);
    } else if (contactFilter === 'missing') {
      result = result.filter((c) => !hasReachableContact(c));
    }
    
    // Apply column filters (checkbox-based)
    Object.entries(columnFilters).forEach(([key, values]) => {
      if (values && values.length > 0) {
        result = result.filter(item => {
          if (key === 'state') {
            const provs = item.provinces;
            if (Array.isArray(provs) && provs.length > 0) {
              return provs.some(p => values.includes(String(p)));
            }
            return item.state != null && values.includes(String(item.state));
          }
          const val = item[key];
          return val !== null && val !== undefined && values.includes(String(val));
        });
      }
    });
    
    // Apply text/numeric filters (operator-based)
    Object.entries(filterMode).forEach(([key, mode]) => {
      const text = filterText[key] || '';
      const isNumericOperator = ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'].includes(mode);
      
      // For empty/notEmpty, text is not required
      if (!text && !['empty', 'notEmpty'].includes(mode)) return;
      
      result = result.filter(item => {
        let val = item[key];
        if (key === 'state') {
          const provs = item.provinces;
          val = (Array.isArray(provs) && provs.length > 0) ? provs.join(', ') : item.state;
        }
        
        // Handle numeric operators
        if (isNumericOperator) {
          const numVal = parseFloat(val);
          const filterNum = parseFloat(text);
          if (isNaN(numVal)) return mode === 'neq';
          if (isNaN(filterNum)) return true;
          switch (mode) {
            case 'eq': return numVal === filterNum;
            case 'neq': return numVal !== filterNum;
            case 'gt': return numVal > filterNum;
            case 'gte': return numVal >= filterNum;
            case 'lt': return numVal < filterNum;
            case 'lte': return numVal <= filterNum;
            default: return true;
          }
        }
        
        // Text operators
        const strVal = val !== null && val !== undefined ? String(val).toLowerCase() : '';
        const textLower = text.toLowerCase();
        switch (mode) {
          case 'equals': return strVal === textLower;
          case 'notEquals': return strVal !== textLower;
          case 'contains': return strVal.includes(textLower);
          case 'notContains': return !strVal.includes(textLower);
          case 'startsWith': return strVal.startsWith(textLower);
          case 'endsWith': return strVal.endsWith(textLower);
          case 'empty': return !strVal;
          case 'notEmpty': return !!strVal;
          default: return true;
        }
      });
    });
    
    // Apply sorting
    if (sortConfig.key && sortConfig.direction) {
      // Date/timestamp columns must be compared as dates. parseFloat() on an ISO
      // string like "2026-05-24T..." returns the leading year (2026) for every row,
      // so without this they would never actually sort.
      const DATE_COLUMNS = ['updated_at', 'created_at', 'last_discovered_at'];
      const isDateCol = DATE_COLUMNS.includes(sortConfig.key);
      result.sort((a, b) => {
        const aVal = a[sortConfig.key];
        const bVal = b[sortConfig.key];
        if (aVal === null || aVal === undefined || aVal === '') return 1;
        if (bVal === null || bVal === undefined || bVal === '') return -1;

        if (isDateCol) {
          const aTime = new Date(aVal).getTime();
          const bTime = new Date(bVal).getTime();
          if (!isNaN(aTime) && !isNaN(bTime)) {
            return sortConfig.direction === 'asc' ? aTime - bTime : bTime - aTime;
          }
        } else {
          // Check if numeric
          const aNum = parseFloat(aVal);
          const bNum = parseFloat(bVal);
          if (!isNaN(aNum) && !isNaN(bNum)) {
            return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
          }
        }
        
        // String comparison
        const strA = String(aVal).toLowerCase();
        const strB = String(bVal).toLowerCase();
        return sortConfig.direction === 'asc' ? strA.localeCompare(strB) : strB.localeCompare(strA);
      });
    }
    
    return result;
  }, [contractors, searchTerm, filterCountry, filterDiscipline, contactFilter, columnFilters, filterMode, filterText, sortConfig]);

  const handleEditCancel = () => {
    setEditingId(null);
    setEditForm({});
  };

  // Get unique countries and disciplines for filters
  const uniqueCountries = [...new Set(contractors.map(c => c.country))].filter(Boolean).sort();
  const uniqueDisciplines = [...new Set(contractors.map(c => c.discipline))].filter(Boolean).sort();

  return (
    <div style={{ padding: '1rem' }}>
      {/* Toast */}
      {toast.show && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          padding: '1rem 1.5rem',
          backgroundColor: toast.type === 'success' ? '#28a745' : '#dc3545',
          color: 'white',
          borderRadius: '6px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 9999,
          animation: 'slideIn 0.3s ease'
        }}>
          {toast.message}
        </div>
      )}
      
      <ConfirmDialog {...dialogProps} />

      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1.5rem',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <div>
          <h2 style={{ margin: 0, color: '#333', display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            {isSupplier ? '📦' : '🏢'} Selected {entityLabelPlural} Database
            <select
              value={viewType}
              onChange={(e) => { setViewType(e.target.value); setSelectedRows(new Set()); }}
              title="Switch between subcontractors and suppliers"
              style={{
                padding: '4px 10px',
                fontSize: '0.85rem',
                fontWeight: 600,
                color: '#374151',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                backgroundColor: '#f9fafb',
                cursor: 'pointer'
              }}
            >
              <option value="subcontractor">Subcontractors (install in field)</option>
              <option value="supplier">Suppliers / Manufacturers (materials)</option>
            </select>
          </h2>
          <p style={{ margin: '0.5rem 0 0', color: '#666', fontSize: '0.9rem' }}>
            {isSupplier
              ? 'Company-wide supplier / manufacturer management and evaluation'
              : 'Company-wide contractor management and evaluation'}
          </p>
        </div>
        
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <ActiveViewers viewers={viewers} isConnected={isConnected} connectionState={connectionState} maxVisible={4} showConnectionStatus={true} />
          {permissions.export && (
            <button
              onClick={handleExport}
              style={{
                padding: '8px 16px',
                backgroundColor: 'white',
                color: '#374151',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.15s ease'
              }}
            >
              📤 Export
            </button>
          )}
          {permissions.import && (
            <button
              onClick={() => setShowImportModal(true)}
              style={{
                padding: '8px 16px',
                backgroundColor: 'white',
                color: '#374151',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.15s ease'
              }}
            >
              📥 Import
            </button>
          )}
          <button
            onClick={() => setShowAuditModal(true)}
            style={{
              padding: '8px 16px',
              backgroundColor: 'white',
              color: '#374151',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.15s ease'
            }}
          >
            📋 Audit Log
          </button>
          {permissions.edit && selectedRows.size === 1 && (
            <button
              onClick={() => {
                const selectedId = Array.from(selectedRows)[0];
                const contractor = contractors.find(c => c.id === selectedId);
                if (contractor) {
                  setEditModalContractor({
                    ...contractor,
                    contacts: contractor.contacts && contractor.contacts.length > 0
                      ? contractor.contacts
                      : [{ contact_name: contractor.contact_name || '', phone: contractor.contact_phone || '', email: contractor.contact_email || '', province_state: '' }]
                  });
                  setEditModalFiles([]);
                }
              }}
              style={{
                padding: '8px 16px',
                backgroundColor: '#007bff',
                color: 'white',
                border: '1px solid #007bff',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              ✏️ Edit
            </button>
          )}
          {permissions.delete && selectedRows.size > 0 && (
            <button
              onClick={handleBulkDelete}
              style={{
                padding: '8px 16px',
                backgroundColor: '#dc3545',
                color: 'white',
                border: '1px solid #dc3545',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              🗑️ Delete ({selectedRows.size})
            </button>
          )}
          {permissions.weights && (
            <button
              onClick={onOpenCriteriaModal}
              style={{
                padding: '8px 16px',
                backgroundColor: 'white',
                color: '#374151',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.15s ease'
              }}
            >
              ⚖️ Criteria
            </button>
          )}
          <button
            onClick={() => setShowColumnConfig(!showColumnConfig)}
            style={{
              padding: '8px 16px',
              backgroundColor: 'white',
              color: '#374151',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.15s ease'
            }}
          >
            ⚙️ Columns
          </button>
          <button
            onClick={autoFitColumns}
            title="Auto-fit column widths to content"
            style={{
              padding: '8px 16px',
              backgroundColor: 'white',
              color: '#374151',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.15s ease'
            }}
          >
            ↔️ Auto-fit
          </button>
          <button
            onClick={resetColumnWidths}
            title="Reset column widths to default"
            style={{
              padding: '8px 16px',
              backgroundColor: 'white',
              color: '#374151',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.15s ease'
            }}
          >
            🔄 Reset
          </button>
          <button
            onClick={undoColumnResize}
            disabled={columnWidthHistory.length === 0}
            title="Undo last column resize (Ctrl+Z)"
            style={{
              padding: '8px 16px',
              backgroundColor: 'white',
              color: columnWidthHistory.length === 0 ? '#9ca3af' : '#374151',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              cursor: columnWidthHistory.length === 0 ? 'not-allowed' : 'pointer',
              fontSize: '13px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              opacity: columnWidthHistory.length === 0 ? 0.6 : 1,
              transition: 'all 0.15s ease'
            }}
          >
            ↩️ Undo
          </button>
          <button
            onClick={() => setShowHelpModal(true)}
            title="Help - Learn how to use this dashboard"
            style={{
              padding: '8px 16px',
              backgroundColor: 'white',
              color: '#374151',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.15s ease'
            }}
          >
            ❓ Help
          </button>
          {permissions.edit && (
            <button
              onClick={() => setShowAddForm(true)}
              style={{
                padding: '8px 16px',
                backgroundColor: 'white',
                color: '#374151',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.15s ease'
              }}
            >
              ➕ Add {entityLabel}
            </button>
          )}
        </div>
      </div>

      {/* Column Config Modal */}
      {showColumnConfig && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowColumnConfig(false)}
        >
          <div 
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '1.5rem',
              minWidth: '400px',
              maxWidth: '500px',
              boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', borderBottom: '1px solid #dee2e6', paddingBottom: '0.75rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem' }}>Column Configuration</h3>
              <button
                onClick={() => setShowColumnConfig(false)}
                style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#666' }}
              >
                ×
              </button>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem' }}>
              <button
                onClick={() => setVisibleColumns(prev => Object.keys(prev).reduce((acc, key) => ({ ...acc, [key]: true }), {}))}
                style={{ padding: '0.4rem 0.75rem', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem' }}
              >
                Show All
              </button>
              <button
                onClick={() => setVisibleColumns(prev => Object.keys(prev).reduce((acc, key) => ({ ...acc, [key]: false }), {}))}
                style={{ padding: '0.4rem 0.75rem', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem' }}
              >
                Hide All
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem' }}>
              {[
                { key: 'id', label: 'ID' },
                { key: 'country', label: 'Country' },
                { key: 'state', label: 'Province/State' },
                { key: 'discipline', label: 'Discipline' },
                { key: 'contractor', label: 'Contractor' },
                { key: 'contact_name', label: 'Contact' },
                { key: 'contact_phone', label: 'Phone' },
                { key: 'contact_email', label: 'Email' },
                { key: 'quality', label: 'Quality' },
                { key: 'hse', label: 'HSE' },
                { key: 'performance', label: 'Performance' },
                { key: 'commercial', label: 'Commercial' },
                { key: 'technical', label: 'Technical' },
                { key: 'insurance', label: 'Insurance' },
                { key: 'orientation', label: 'Orientation' },
                { key: 'winloss', label: 'Win/Loss' },
                { key: 'overall', label: 'Score' },
                { key: 'updated_by', label: 'Updated By' },
                { key: 'updated_at', label: 'Updated At' },
                { key: 'discovery_comments', label: 'Comments' },
                { key: 'discovery_job_number', label: 'Job #' },
                { key: 'discovery_year', label: 'Year' },
                { key: 'last_bid', label: 'Last Bid' },
                { key: 'last_project', label: 'Last Project' },
                { key: 'last_year', label: 'Last Year' },
                { key: 'source_file_url', label: 'Source File' },
              ].map(col => (
                <label key={col.key} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', cursor: 'pointer', padding: '0.25rem' }}>
                  <input
                    type="checkbox"
                    checked={visibleColumns[col.key]}
                    onChange={() => setVisibleColumns(prev => ({ ...prev, [col.key]: !prev[col.key] }))}
                  />
                  {col.label}
                </label>
              ))}
            </div>
            <div style={{ marginTop: '1rem', textAlign: 'right' }}>
              <button
                onClick={() => setShowColumnConfig(false)}
                style={{ padding: '0.5rem 1rem', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.9rem' }}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={{
        backgroundColor: '#f8f9fa',
        padding: '1rem',
        borderRadius: '6px',
        marginBottom: '1rem',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '1rem',
        alignItems: 'center'
      }}>
        <div>
          <label style={{ display: 'block', fontSize: '0.8rem', color: '#666', marginBottom: '0.25rem' }}>
            Search
          </label>
          <input
            type="text"
            placeholder="Search contractors..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              padding: '0.5rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              width: '200px'
            }}
          />
        </div>
        
        <div>
          <label style={{ display: 'block', fontSize: '0.8rem', color: '#666', marginBottom: '0.25rem' }}>
            Country
          </label>
          <select
            value={filterCountry}
            onChange={(e) => setFilterCountry(e.target.value)}
            style={{
              padding: '0.5rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              minWidth: '150px'
            }}
          >
            <option value="">All Countries</option>
            {uniqueCountries.map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
        
        <div>
          <label style={{ display: 'block', fontSize: '0.8rem', color: '#666', marginBottom: '0.25rem' }}>
            Discipline
          </label>
          <select
            value={filterDiscipline}
            onChange={(e) => setFilterDiscipline(e.target.value)}
            style={{
              padding: '0.5rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              minWidth: '150px'
            }}
          >
            <option value="">All Disciplines</option>
            {uniqueDisciplines.map(d => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </div>

        <div>
          <label style={{ display: 'block', fontSize: '0.8rem', color: '#666', marginBottom: '0.25rem' }}>
            Contact
          </label>
          <select
            value={contactFilter}
            onChange={(e) => setContactFilter(e.target.value)}
            style={{
              padding: '0.5rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              minWidth: '180px'
            }}
            title="Default hides contractors with no email or phone"
          >
            <option value="has">Has email or phone</option>
            <option value="missing">Missing contact</option>
            <option value="all">All contractors</option>
          </select>
        </div>
        
        <div style={{ marginLeft: 'auto' }}>
          <span style={{ fontSize: '0.9rem', color: '#666' }}>
            Total: <strong>{filteredContractors.length}</strong> {entityLabelPlural.toLowerCase()}
            {filteredContractors.length !== contractors.length && (
              <span> (filtered from {contractors.length})</span>
            )}
          </span>
        </div>
      </div>

      {/* Active Filters Display - Fixed position top right */}
      {hasActiveFilters && (
        <div style={{
          position: 'fixed',
          top: '120px',
          right: '40px',
          zIndex: 1000,
          display: 'flex',
          flexWrap: 'wrap',
          gap: '6px',
          padding: '8px 12px',
          backgroundColor: '#d1fae5',
          borderRadius: '8px',
          border: '1px solid #a7f3d0',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          maxWidth: '500px',
          alignItems: 'center'
        }}>
          <span style={{ fontSize: '12px', color: '#065f46', fontWeight: '600' }}>🔍 Filters:</span>
          {activeFilters.map((filter, idx) => (
            <span key={idx} style={{
              padding: '2px 8px',
              backgroundColor: filter.type === 'values' ? '#17a2b8' : '#28a745',
              color: 'white',
              borderRadius: '12px',
              fontSize: '11px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              {filter.key}: {filter.type === 'values' ? `${filter.count} selected` : `${filter.mode} "${filter.value}"`}
              <button onClick={() => clearColumnFilter(filter.key)} style={{
                background: 'none',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                padding: 0,
                fontSize: '12px',
                lineHeight: 1,
                fontWeight: 'bold'
              }}>×</button>
            </span>
          ))}
          {searchTerm && (
            <span style={{
              padding: '2px 8px',
              backgroundColor: '#007bff',
              color: 'white',
              borderRadius: '12px',
              fontSize: '11px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              Search: "{searchTerm}"
              <button onClick={() => setSearchTerm('')} style={{
                background: 'none',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                padding: 0,
                fontSize: '12px',
                lineHeight: 1,
                fontWeight: 'bold'
              }}>×</button>
            </span>
          )}
          {filterCountry && (
            <span style={{
              padding: '2px 8px',
              backgroundColor: '#fd7e14',
              color: 'white',
              borderRadius: '12px',
              fontSize: '11px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              Country: {filterCountry}
              <button onClick={() => setFilterCountry('')} style={{
                background: 'none',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                padding: 0,
                fontSize: '12px',
                lineHeight: 1,
                fontWeight: 'bold'
              }}>×</button>
            </span>
          )}
          {filterDiscipline && (
            <span style={{
              padding: '2px 8px',
              backgroundColor: '#6f42c1',
              color: 'white',
              borderRadius: '12px',
              fontSize: '11px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              Discipline: {filterDiscipline}
              <button onClick={() => setFilterDiscipline('')} style={{
                background: 'none',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                padding: 0,
                fontSize: '12px',
                lineHeight: 1,
                fontWeight: 'bold'
              }}>×</button>
            </span>
          )}
          <button onClick={clearAllFilters} style={{
            padding: '2px 8px',
            backgroundColor: '#dc3545',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '11px',
            fontWeight: 'bold'
          }}>
            × Clear All
          </button>
        </div>
      )}

      {/* Add Contractor Modal */}
      {showAddForm && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowAddForm(false)}
        >
          <div 
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '1.5rem',
              width: '90%',
              maxWidth: '800px',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', borderBottom: '1px solid #dee2e6', paddingBottom: '0.75rem' }}>
              <h4 style={{ margin: 0, color: '#28a745' }}>➕ Add New {entityLabel}</h4>
              <button
                onClick={() => setShowAddForm(false)}
                style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#666' }}
              >
                ×
              </button>
            </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.25rem', fontWeight: 'bold' }}>Country *</label>
              <select
                value={newContractor.country}
                onChange={(e) => {
                  const newCountry = e.target.value;
                  setNewContractor({ 
                    ...newContractor, 
                    country: newCountry,
                    provinces: newCountry !== newContractor.country ? [] : newContractor.provinces
                  });
                }}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
              >
                <option value="">Select Country</option>
                {COUNTRIES.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.25rem', fontWeight: 'bold' }}>Discipline *</label>
              <select
                value={newContractor.discipline}
                onChange={(e) => setNewContractor({ ...newContractor, discipline: e.target.value })}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
              >
                <option value="">Select Discipline</option>
                {disciplines.map(d => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.25rem', fontWeight: 'bold' }}>Contractor Name *</label>
              <input
                type="text"
                value={newContractor.contractor}
                onChange={(e) => setNewContractor({ ...newContractor, contractor: e.target.value })}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.25rem' }}>Insurance</label>
              <YesNoCheckbox value={newContractor.insurance} onChange={(v) => setNewContractor({ ...newContractor, insurance: v })} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.25rem' }}>Quality (1-5)</label>
              <ScoreInput value={newContractor.quality} onChange={(v) => setNewContractor({ ...newContractor, quality: v })} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.25rem' }}>HSE (1-5)</label>
              <ScoreInput value={newContractor.hse} onChange={(v) => setNewContractor({ ...newContractor, hse: v })} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.25rem' }}>Orientation</label>
              <YesNoCheckbox value={newContractor.orientation} onChange={(v) => setNewContractor({ ...newContractor, orientation: v })} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.25rem' }}>Performance (1-5)</label>
              <ScoreInput value={newContractor.performance} onChange={(v) => setNewContractor({ ...newContractor, performance: v })} />
            </div>
          </div>
          
          {/* Province/State Multi-Select */}
          {newContractor.country && getProvincesForCountry(newContractor.country).length > 0 && (
            <div style={{ marginTop: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                Province/State (select all that apply)
              </label>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', 
                gap: '0.35rem',
                padding: '0.75rem',
                backgroundColor: '#f8f9fa',
                borderRadius: '6px',
                border: '1px solid #e9ecef',
                maxHeight: '150px',
                overflowY: 'auto'
              }}>
                {getProvincesForCountry(newContractor.country).map(province => (
                  <label 
                    key={province} 
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '0.35rem',
                      cursor: 'pointer',
                      fontSize: '0.8rem',
                      padding: '0.2rem 0.35rem',
                      borderRadius: '4px',
                      backgroundColor: (newContractor.provinces || []).includes(province) ? '#e3f2fd' : 'transparent'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={(newContractor.provinces || []).includes(province)}
                      onChange={(e) => {
                        const currentProvinces = newContractor.provinces || [];
                        if (e.target.checked) {
                          setNewContractor({ ...newContractor, provinces: [...currentProvinces, province] });
                        } else {
                          setNewContractor({ ...newContractor, provinces: currentProvinces.filter(p => p !== province) });
                        }
                      }}
                      style={{ cursor: 'pointer' }}
                    />
                    {province}
                  </label>
                ))}
              </div>
              {(newContractor.provinces || []).length > 0 && (
                <div style={{ marginTop: '0.35rem', fontSize: '0.75rem', color: '#666' }}>
                  Selected: {newContractor.provinces.join(', ')}
                </div>
              )}
            </div>
          )}
          <div style={{ marginTop: '1rem' }}>
            <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.25rem' }}>Notes</label>
            <textarea
              value={newContractor.notes}
              onChange={(e) => setNewContractor({ ...newContractor, notes: e.target.value })}
              rows={2}
              style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>
          {/* Company Contacts Section - Multiple Contacts */}
          <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #e9ecef' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <h5 style={{ margin: 0, color: '#495057', fontSize: '0.95rem', fontWeight: 'bold' }}>📞 Company Contacts</h5>
              <button
                type="button"
                onClick={() => setNewContractor({
                  ...newContractor,
                  contacts: [...(newContractor.contacts || []), { contact_name: '', phone: '', email: '', province_state: '' }]
                })}
                style={{
                  padding: '0.25rem 0.5rem',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.8rem'
                }}
              >
                + Add Contact
              </button>
            </div>
            {(newContractor.contacts || []).map((contact, idx) => (
              <div key={idx} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  placeholder="Contact name"
                  value={contact.contact_name || ''}
                  onChange={(e) => {
                    const updatedContacts = [...newContractor.contacts];
                    updatedContacts[idx] = { ...updatedContacts[idx], contact_name: e.target.value };
                    setNewContractor({ ...newContractor, contacts: updatedContacts });
                  }}
                  style={{ flex: '1 1 120px', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.85rem' }}
                />
                <input
                  type="text"
                  placeholder="(xxx)-xxx-xxxx"
                  value={contact.phone || ''}
                  onChange={(e) => {
                    const updatedContacts = [...newContractor.contacts];
                    updatedContacts[idx] = { ...updatedContacts[idx], phone: formatPhoneNumber(e.target.value) };
                    setNewContractor({ ...newContractor, contacts: updatedContacts });
                  }}
                  style={{ flex: '1 1 120px', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.85rem' }}
                />
                <input
                  type="email"
                  placeholder="Email address"
                  value={contact.email || ''}
                  onChange={(e) => {
                    const updatedContacts = [...newContractor.contacts];
                    updatedContacts[idx] = { ...updatedContacts[idx], email: e.target.value };
                    setNewContractor({ ...newContractor, contacts: updatedContacts });
                  }}
                  style={{ flex: '1.5 1 160px', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.85rem' }}
                />
                {newContractor.country && getProvincesForCountry(newContractor.country).length > 0 && (
                  <select
                    value={contact.province_state || ''}
                    onChange={(e) => {
                      const updatedContacts = [...newContractor.contacts];
                      updatedContacts[idx] = { ...updatedContacts[idx], province_state: e.target.value };
                      setNewContractor({ ...newContractor, contacts: updatedContacts });
                    }}
                    style={{ flex: '1 1 120px', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.85rem' }}
                  >
                    <option value="">Province/State</option>
                    {getProvincesForCountry(newContractor.country).map(p => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                )}
                {newContractor.contacts.length > 1 && (
                  <button
                    type="button"
                    onClick={() => {
                      const updatedContacts = newContractor.contacts.filter((_, i) => i !== idx);
                      setNewContractor({ ...newContractor, contacts: updatedContacts });
                    }}
                    style={{
                      padding: '0.35rem 0.5rem',
                      backgroundColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.75rem'
                    }}
                  >
                    🗑️
                  </button>
                )}
              </div>
            ))}
          </div>
          
          {/* Attachments Section */}
          <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#fff3e0', borderRadius: '6px', border: '1px solid #ffe0b2' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <h5 style={{ margin: 0, color: '#e65100', fontSize: '0.95rem', fontWeight: 'bold' }}>📎 Attachments</h5>
              <div>
                <input
                  type="file"
                  ref={newContractorFileInputRef}
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    setNewContractorFiles(prev => [...prev, ...files]);
                    if (newContractorFileInputRef.current) newContractorFileInputRef.current.value = '';
                  }}
                  accept=".pdf,.doc,.docx,.xls,.xlsx"
                  multiple
                  style={{ display: 'none' }}
                />
                <button
                  type="button"
                  onClick={() => newContractorFileInputRef.current?.click()}
                  style={{
                    padding: '0.25rem 0.5rem',
                    backgroundColor: '#ff9800',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.8rem'
                  }}
                >
                  + Add Files
                </button>
              </div>
            </div>
            <div style={{ fontSize: '0.75rem', color: '#666', marginBottom: '0.5rem' }}>
              Allowed: PDF, Word (.doc, .docx), Excel (.xls, .xlsx) - Max 10MB each
            </div>
            {newContractorFiles.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '0.5rem', color: '#999', fontSize: '0.85rem' }}>
                No files selected. Click "Add Files" to attach documents.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                {newContractorFiles.map((file, idx) => {
                  const fileInfo = getFileIcon(file.type, file.name);
                  return (
                    <div key={idx} style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0.5rem',
                      backgroundColor: 'white',
                      borderRadius: '4px',
                      border: '1px solid #ffe0b2'
                    }}>
                      <span style={{ fontSize: '1.2rem', marginRight: '0.5rem', color: fileInfo.color }}>{fileInfo.icon}</span>
                      <div style={{ flex: 1, fontSize: '0.85rem' }}>{file.name}</div>
                      <div style={{ fontSize: '0.75rem', color: '#666', marginRight: '0.5rem' }}>{formatFileSize(file.size)}</div>
                      <button
                        type="button"
                        onClick={() => setNewContractorFiles(prev => prev.filter((_, i) => i !== idx))}
                        style={{
                          padding: '0.25rem 0.35rem',
                          backgroundColor: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.7rem'
                        }}
                      >
                        🗑️
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div style={{ marginTop: '1.5rem', display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', borderTop: '1px solid #dee2e6', paddingTop: '1rem' }}>
            <button
              onClick={() => {
                setShowAddForm(false);
                setNewContractor({
                  country: '', state: '', discipline: '', contractor: '',
                  insurance: '', quality: '', hse: '', orientation: '', performance: '',
                  notes: '', contacts: [{ contact_name: '', phone: '', email: '' }]
                });
                setNewContractorFiles([]);
              }}
              style={{
                padding: '0.5rem 1.25rem',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleAddContractor}
              disabled={saving}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.7 : 1
              }}
            >
              {saving ? 'Adding...' : `➕ Add ${entityLabel}`}
            </button>
          </div>
          </div>
        </div>
      )}

      {/* Edit Contractor Modal */}
      {editModalContractor && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setEditModalContractor(null)}
        >
          <div 
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '1.5rem',
              width: '90%',
              maxWidth: '800px',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', borderBottom: '1px solid #dee2e6', paddingBottom: '0.75rem' }}>
              <h4 style={{ margin: 0, color: '#007bff' }}>✏️ Edit Contractor (ID: {editModalContractor.id})</h4>
              <button
                onClick={() => setEditModalContractor(null)}
                style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#666' }}
              >
                ×
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.25rem', fontWeight: 'bold' }}>Country *</label>
                <select
                  value={editModalContractor.country || ''}
                  onChange={(e) => {
                    const newCountry = e.target.value;
                    setEditModalContractor(prev => ({ 
                      ...prev, 
                      country: newCountry,
                      provinces: newCountry !== prev.country ? [] : prev.provinces
                    }));
                  }}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                >
                  <option value="">Select Country</option>
                  {COUNTRIES.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.25rem', fontWeight: 'bold' }}>Discipline *</label>
                <select
                  value={editModalContractor.discipline || ''}
                  onChange={(e) => setEditModalContractor(prev => ({ ...prev, discipline: e.target.value }))}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                >
                  <option value="">Select Discipline</option>
                  {disciplines.map(d => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.25rem', fontWeight: 'bold' }}>Contractor Name *</label>
                <input
                  type="text"
                  value={editModalContractor.contractor || ''}
                  onChange={(e) => setEditModalContractor(prev => ({ ...prev, contractor: e.target.value }))}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.25rem' }}>Insurance</label>
                <YesNoCheckbox value={editModalContractor.insurance} onChange={(v) => setEditModalContractor(prev => ({ ...prev, insurance: v }))} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.25rem' }}>Quality (1-5)</label>
                <ScoreInput value={editModalContractor.quality} onChange={(v) => setEditModalContractor(prev => ({ ...prev, quality: v }))} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.25rem' }}>HSE (1-5)</label>
                <ScoreInput value={editModalContractor.hse} onChange={(v) => setEditModalContractor(prev => ({ ...prev, hse: v }))} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.25rem' }}>Orientation</label>
                <YesNoCheckbox value={editModalContractor.orientation} onChange={(v) => setEditModalContractor(prev => ({ ...prev, orientation: v }))} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.25rem' }}>Performance (1-5)</label>
                <ScoreInput value={editModalContractor.performance} onChange={(v) => setEditModalContractor(prev => ({ ...prev, performance: v }))} />
              </div>
            </div>
            
            {/* Province/State Multi-Select */}
            {editModalContractor.country && getProvincesForCountry(editModalContractor.country).length > 0 && (
              <div style={{ marginTop: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                  Province/State (select all that apply)
                </label>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', 
                  gap: '0.35rem',
                  padding: '0.75rem',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '6px',
                  border: '1px solid #e9ecef',
                  maxHeight: '150px',
                  overflowY: 'auto'
                }}>
                  {getProvincesForCountry(editModalContractor.country).map(province => (
                    <label 
                      key={province} 
                      style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '0.35rem',
                        cursor: 'pointer',
                        fontSize: '0.8rem',
                        padding: '0.2rem 0.35rem',
                        borderRadius: '4px',
                        backgroundColor: (editModalContractor.provinces || []).includes(province) ? '#e3f2fd' : 'transparent'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={(editModalContractor.provinces || []).includes(province)}
                        onChange={(e) => {
                          const currentProvinces = editModalContractor.provinces || [];
                          if (e.target.checked) {
                            setEditModalContractor(prev => ({ ...prev, provinces: [...currentProvinces, province] }));
                          } else {
                            setEditModalContractor(prev => ({ ...prev, provinces: currentProvinces.filter(p => p !== province) }));
                          }
                        }}
                        style={{ cursor: 'pointer' }}
                      />
                      {province}
                    </label>
                  ))}
                </div>
                {(editModalContractor.provinces || []).length > 0 && (
                  <div style={{ marginTop: '0.35rem', fontSize: '0.75rem', color: '#666' }}>
                    Selected: {editModalContractor.provinces.join(', ')}
                  </div>
                )}
              </div>
            )}
            <div style={{ marginTop: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.25rem' }}>Notes</label>
              <textarea
                value={editModalContractor.notes || ''}
                onChange={(e) => setEditModalContractor(prev => ({ ...prev, notes: e.target.value }))}
                rows={2}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
              />
            </div>
            {/* Company Contacts Section */}
            <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #e9ecef' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <h5 style={{ margin: 0, color: '#495057', fontSize: '0.95rem', fontWeight: 'bold' }}>📞 Company Contacts</h5>
                <button
                  type="button"
                  onClick={() => setEditModalContractor(prev => ({
                    ...prev,
                    contacts: [...(prev.contacts || []), { contact_name: '', phone: '', email: '', province_state: '' }]
                  }))}
                  style={{
                    padding: '0.25rem 0.5rem',
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.8rem'
                  }}
                >
                  + Add Contact
                </button>
              </div>
              {(editModalContractor.contacts || []).map((contact, idx) => (
                <div key={idx} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                  <input
                    type="text"
                    placeholder="Contact name"
                    value={contact.contact_name || ''}
                    onChange={(e) => {
                      const updatedContacts = [...(editModalContractor.contacts || [])];
                      updatedContacts[idx] = { ...updatedContacts[idx], contact_name: e.target.value };
                      setEditModalContractor(prev => ({ ...prev, contacts: updatedContacts }));
                    }}
                    style={{ flex: '1 1 120px', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.85rem' }}
                  />
                  <input
                    type="text"
                    placeholder="(xxx)-xxx-xxxx"
                    value={contact.phone || ''}
                    onChange={(e) => {
                      const updatedContacts = [...(editModalContractor.contacts || [])];
                      updatedContacts[idx] = { ...updatedContacts[idx], phone: formatPhoneNumber(e.target.value) };
                      setEditModalContractor(prev => ({ ...prev, contacts: updatedContacts }));
                    }}
                    style={{ flex: '1 1 120px', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.85rem' }}
                  />
                  <input
                    type="email"
                    placeholder="Email address"
                    value={contact.email || ''}
                    onChange={(e) => {
                      const updatedContacts = [...(editModalContractor.contacts || [])];
                      updatedContacts[idx] = { ...updatedContacts[idx], email: e.target.value };
                      setEditModalContractor(prev => ({ ...prev, contacts: updatedContacts }));
                    }}
                    style={{ flex: '1.5 1 160px', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.85rem' }}
                  />
                  {editModalContractor.country && getProvincesForCountry(editModalContractor.country).length > 0 && (
                    <select
                      value={contact.province_state || ''}
                      onChange={(e) => {
                        const updatedContacts = [...(editModalContractor.contacts || [])];
                        updatedContacts[idx] = { ...updatedContacts[idx], province_state: e.target.value };
                        setEditModalContractor(prev => ({ ...prev, contacts: updatedContacts }));
                      }}
                      style={{ flex: '1 1 120px', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.85rem' }}
                    >
                      <option value="">Province/State</option>
                      {getProvincesForCountry(editModalContractor.country).map(p => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                  )}
                  {(editModalContractor.contacts || []).length > 1 && (
                    <button
                      type="button"
                      onClick={() => {
                        const updatedContacts = (editModalContractor.contacts || []).filter((_, i) => i !== idx);
                        setEditModalContractor(prev => ({ ...prev, contacts: updatedContacts }));
                      }}
                      style={{
                        padding: '0.35rem 0.5rem',
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.75rem'
                      }}
                    >
                      🗑️
                    </button>
                  )}
                </div>
              ))}
            </div>
            
            {/* Attachments Section */}
            <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#fff3e0', borderRadius: '6px', border: '1px solid #ffe0b2' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <h5 style={{ margin: 0, color: '#e65100', fontSize: '0.95rem', fontWeight: 'bold' }}>📎 Attachments</h5>
                <div>
                  <input
                    type="file"
                    ref={editModalFileInputRef}
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      setEditModalFiles(prev => [...prev, ...files]);
                      if (editModalFileInputRef.current) editModalFileInputRef.current.value = '';
                    }}
                    accept=".pdf,.doc,.docx,.xls,.xlsx"
                    multiple
                    style={{ display: 'none' }}
                  />
                  <button
                    type="button"
                    onClick={() => editModalFileInputRef.current?.click()}
                    style={{
                      padding: '0.25rem 0.5rem',
                      backgroundColor: '#ff9800',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.8rem'
                    }}
                  >
                    + Add Files
                  </button>
                </div>
              </div>
              <div style={{ fontSize: '0.75rem', color: '#666', marginBottom: '0.5rem' }}>
                Allowed: PDF, Word (.doc, .docx), Excel (.xls, .xlsx) - Max 10MB each
              </div>
              {editModalFiles.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '0.5rem', color: '#999', fontSize: '0.85rem' }}>
                  No new files selected. Click "Add Files" to attach documents.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  {editModalFiles.map((file, idx) => {
                    const fileInfo = getFileIcon(file.type, file.name);
                    return (
                      <div key={idx} style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0.5rem',
                        backgroundColor: 'white',
                        borderRadius: '4px',
                        border: '1px solid #ffe0b2'
                      }}>
                        <span style={{ fontSize: '1.2rem', marginRight: '0.5rem', color: fileInfo.color }}>{fileInfo.icon}</span>
                        <div style={{ flex: 1, fontSize: '0.85rem' }}>{file.name}</div>
                        <div style={{ fontSize: '0.75rem', color: '#666', marginRight: '0.5rem' }}>{formatFileSize(file.size)}</div>
                        <button
                          type="button"
                          onClick={() => setEditModalFiles(prev => prev.filter((_, i) => i !== idx))}
                          style={{
                            padding: '0.25rem 0.35rem',
                            backgroundColor: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.7rem'
                          }}
                        >
                          🗑️
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div style={{ marginTop: '1.5rem', display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', borderTop: '1px solid #dee2e6', paddingTop: '1rem' }}>
              <button
                onClick={() => setEditModalContractor(null)}
                style={{
                  padding: '0.5rem 1.25rem',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleEditModalSave}
                disabled={saving}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  opacity: saving ? 0.7 : 1
                }}
              >
                {saving ? 'Saving...' : '💾 Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <div style={{
            display: 'inline-block',
            width: '40px',
            height: '40px',
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #007bff',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{ marginTop: '1rem', color: '#666' }}>Loading contractors...</p>
        </div>
      ) : error ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#dc3545' }}>
          <p>{error}</p>
          <button onClick={fetchContractors} style={{ marginTop: '1rem', padding: '0.5rem 1rem' }}>
            Retry
          </button>
        </div>
      ) : contractors.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '3rem',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px'
        }}>
          <p style={{ fontSize: '1.2rem', color: '#666' }}>No {entityLabelPlural.toLowerCase()} found</p>
          <p style={{ color: '#999' }}>Click "Add {entityLabel}" to add your first {entityLabel.toLowerCase()}</p>
        </div>
      ) : (
        <VirtualizedContractorsTable
          filteredContractors={filteredContractors}
          selectedRows={selectedRows}
          editingId={editingId}
          editForm={editForm}
          setEditForm={setEditForm}
          saving={saving}
          sortConfig={sortConfig}
          columnFilters={columnFilters}
          filterMode={filterMode}
          filterText={filterText}
          openFilterColumn={openFilterColumn}
          filterSearch={filterSearch}
          handleSort={handleSort}
          handleSelectAll={handleSelectAll}
          handleSelectRow={handleSelectRow}
          handleEditStart={handleEditStart}
          handleEditSave={handleEditSave}
          handleEditCancel={handleEditCancel}
          handleContractorClick={handleContractorClick}
          handleScoreClick={handleScoreClick}
          handleFilterToggle={handleFilterToggle}
          selectAllColumnFilter={selectAllColumnFilter}
          clearColumnFilter={clearColumnFilter}
          applyTextFilter={applyTextFilter}
          getUniqueValues={getUniqueValues}
          setOpenFilterColumn={setOpenFilterColumn}
          setFilterSearch={setFilterSearch}
          disciplines={disciplines}
          canDelete={permissions.delete}
          columnWidthsFromParent={columnWidthsState}
          setColumnWidthsFromParent={setColumnWidthsState}
          saveColumnWidthsToHistory={saveColumnWidthsToHistory}
          collapsedGroups={collapsedGroups}
          toggleGroupCollapsed={toggleGroupCollapsed}
          visibleColumns={visibleColumns}
        />
      )}

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes slideIn {
            from { transform: translateX(100%); opacity: 0; }
            to { transform: translateX(0); opacity: 1; }
          }
        `}
      </style>

      {/* Import Modal */}
      {showImportModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '8px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
          }}>
            <h3 style={{ margin: '0 0 1rem 0', color: '#333' }}>📥 Import Contractors</h3>
            <p style={{ margin: '0 0 1.5rem 0', color: '#666', fontSize: '0.9rem' }}>
              Upload an Excel file (.xlsx) containing contractor data. The file should have columns for:
              <strong>Action</strong> (optional: type 'd' to delete), Country, State, Discipline, Contractor, Insurance, Quality, HSE, Orientation, Performance, Notes.
            </p>
            <div style={{ 
              margin: '0 0 1rem 0', 
              padding: '0.75rem', 
              backgroundColor: '#fff3cd', 
              borderRadius: '4px',
              fontSize: '0.85rem',
              color: '#856404'
            }}>
              <strong>Action Column:</strong> Type "d" in the Action column to delete contractors during import.
            </div>
            
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) handleImport(file);
              }}
              disabled={importing}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '2px dashed #007bff',
                borderRadius: '6px',
                marginBottom: '1rem',
                cursor: importing ? 'not-allowed' : 'pointer'
              }}
            />
            
            {importing && (
              <div style={{ 
                textAlign: 'center', 
                color: '#007bff',
                marginBottom: '1rem',
                fontSize: '0.9rem'
              }}>
                Importing contractors...
              </div>
            )}
            
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowImportModal(false)}
                disabled={importing}
                style={{
                  padding: '0.5rem 1.25rem',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: importing ? 'not-allowed' : 'pointer',
                  fontSize: '0.9rem',
                  opacity: importing ? 0.6 : 1
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contractor Detail Modal */}
      <ContractorDetailModal
        isOpen={!!detailModalContractor}
        onClose={() => setDetailModalContractor(null)}
        contractor={detailModalContractor}
        onSave={handleDetailModalSave}
        saving={saving}
        instance={instance}
        accounts={accounts}
        onEvaluationSaved={fetchContractors}
        canEdit={permissions.edit}
      />

      {/* Score History Modal */}
      <ScoreHistoryModal
        isOpen={!!scoreHistoryContractor}
        onClose={() => setScoreHistoryContractor(null)}
        contractorId={scoreHistoryContractor?.id}
        contractorName={scoreHistoryContractor?.contractor}
        instance={instance}
        accounts={accounts}
      />

      {/* Audit Log Modal */}
      <ContractorAuditModal
        isOpen={showAuditModal}
        onClose={() => setShowAuditModal(false)}
        instance={instance}
        accounts={accounts}
      />

      {/* Help Modal */}
      {showHelpModal && (
        <div 
          style={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            backgroundColor: 'rgba(0,0,0,0.6)', 
            zIndex: 100000, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center' 
          }} 
          onClick={() => setShowHelpModal(false)}
        >
          <div 
            style={{ 
              backgroundColor: 'white', 
              borderRadius: '12px', 
              width: '90%', 
              maxWidth: '800px', 
              maxHeight: '85vh', 
              overflowY: 'auto', 
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)' 
            }} 
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{ 
              padding: '1.5rem', 
              borderBottom: '2px solid #17a2b8', 
              background: 'linear-gradient(135deg, #17a2b8, #138496)', 
              color: 'white', 
              borderRadius: '12px 12px 0 0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{ margin: 0, fontSize: '1.5rem' }}>📚 Contractors Dashboard Help</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <ListenButton compact guideId="selected-contractors-help" title="Contractors Dashboard Help" accent="#17a2b8" dark />
                <button 
                  onClick={() => setShowHelpModal(false)} 
                  style={{ 
                    background: 'rgba(255,255,255,0.2)', 
                    border: 'none', 
                    color: 'white', 
                    fontSize: '1.5rem', 
                    cursor: 'pointer', 
                    padding: '0.25rem 0.75rem', 
                    borderRadius: '6px' 
                  }}
                >×</button>
              </div>
            </div>

            {/* Content */}
            <div style={{ padding: '1.5rem' }}>
<HelpWithVideoTour guideId="selected-contractors-help" title="Contractors Dashboard Help" accent="#17a2b8">

              {/* Filtering Section */}
              <div style={{ marginBottom: '2rem', padding: '1rem', backgroundColor: '#e3f2fd', borderRadius: '8px', border: '1px solid #90caf9' }}>
                <h3 style={{ margin: '0 0 1rem 0', color: '#1565c0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  🔍 Filtering Data
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                  <div style={{ backgroundColor: 'white', padding: '0.75rem', borderRadius: '6px' }}>
                    <strong style={{ color: '#1976d2' }}>Column Filters</strong>
                    <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem', color: '#555' }}>
                      Click the <span style={{ backgroundColor: '#ddd', padding: '2px 6px', borderRadius: '3px' }}>🔍</span> icon on any column header to filter.
                    </p>
                  </div>
                  <div style={{ backgroundColor: 'white', padding: '0.75rem', borderRadius: '6px' }}>
                    <strong style={{ color: '#1976d2' }}>Text Filters</strong>
                    <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem', color: '#555' }}>
                      Contains, Equals, Starts with, Ends with, Is empty, etc.
                    </p>
                  </div>
                  <div style={{ backgroundColor: 'white', padding: '0.75rem', borderRadius: '6px' }}>
                    <strong style={{ color: '#1976d2' }}>Numeric Filters</strong>
                    <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem', color: '#555' }}>
                      For score columns: =, ≠, &gt;, ≥, &lt;, ≤
                    </p>
                  </div>
                  <div style={{ backgroundColor: 'white', padding: '0.75rem', borderRadius: '6px' }}>
                    <strong style={{ color: '#1976d2' }}>Close Filter</strong>
                    <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem', color: '#555' }}>
                      Press <kbd style={{ backgroundColor: '#eee', padding: '2px 6px', borderRadius: '3px', border: '1px solid #ccc' }}>ESC</kbd> or click outside
                    </p>
                  </div>
                </div>
              </div>

              {/* Column Resizing Section */}
              <div style={{ marginBottom: '2rem', padding: '1rem', backgroundColor: '#e8f5e9', borderRadius: '8px', border: '1px solid #a5d6a7' }}>
                <h3 style={{ margin: '0 0 1rem 0', color: '#2e7d32', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  ↔️ Column Management
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                  <div style={{ backgroundColor: 'white', padding: '0.75rem', borderRadius: '6px' }}>
                    <strong style={{ color: '#388e3c' }}>Manual Resize</strong>
                    <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem', color: '#555' }}>
                      Drag the column edge (right border) to resize
                    </p>
                  </div>
                  <div style={{ backgroundColor: 'white', padding: '0.75rem', borderRadius: '6px' }}>
                    <strong style={{ color: '#388e3c' }}>↔️ Auto-fit</strong>
                    <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem', color: '#555' }}>
                      Automatically sizes all columns to fit content
                    </p>
                  </div>
                  <div style={{ backgroundColor: 'white', padding: '0.75rem', borderRadius: '6px' }}>
                    <strong style={{ color: '#388e3c' }}>🔄 Reset</strong>
                    <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem', color: '#555' }}>
                      Returns columns to default widths
                    </p>
                  </div>
                  <div style={{ backgroundColor: 'white', padding: '0.75rem', borderRadius: '6px' }}>
                    <strong style={{ color: '#388e3c' }}>↩️ Undo</strong>
                    <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem', color: '#555' }}>
                      Reverts last resize. Also: <kbd style={{ backgroundColor: '#eee', padding: '2px 6px', borderRadius: '3px', border: '1px solid #ccc' }}>Ctrl+Z</kbd>
                    </p>
                  </div>
                </div>
              </div>

              {/* Sorting Section */}
              <div style={{ marginBottom: '2rem', padding: '1rem', backgroundColor: '#fff3e0', borderRadius: '8px', border: '1px solid #ffcc80' }}>
                <h3 style={{ margin: '0 0 1rem 0', color: '#ef6c00', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  🔀 Sorting
                </h3>
                <p style={{ margin: 0, fontSize: '0.95rem', color: '#555' }}>
                  <strong>Click any column name</strong> to sort ascending (▲), click again for descending (▼), and again to clear sorting.
                </p>
              </div>

              {/* Score Columns Section */}
              <div style={{ marginBottom: '2rem', padding: '1rem', backgroundColor: '#fce4ec', borderRadius: '8px', border: '1px solid #f48fb1' }}>
                <h3 style={{ margin: '0 0 1rem 0', color: '#c2185b', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  ⭐ Score Columns
                </h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.75rem' }}>
                  <span style={{ padding: '4px 10px', backgroundColor: '#ffd54f', borderRadius: '12px', fontSize: '0.85rem' }}>⭐ Quality</span>
                  <span style={{ padding: '4px 10px', backgroundColor: '#81c784', borderRadius: '12px', fontSize: '0.85rem' }}>🦺 HSE</span>
                  <span style={{ padding: '4px 10px', backgroundColor: '#64b5f6', borderRadius: '12px', fontSize: '0.85rem' }}>📈 Performance</span>
                  <span style={{ padding: '4px 10px', backgroundColor: '#ba68c8', borderRadius: '12px', fontSize: '0.85rem' }}>💼 Commercial</span>
                  <span style={{ padding: '4px 10px', backgroundColor: '#4db6ac', borderRadius: '12px', fontSize: '0.85rem' }}>📐 Technical</span>
                </div>
                <p style={{ margin: 0, fontSize: '0.9rem', color: '#555' }}>
                  Scores range from 0-5. The <strong>Overall Score</strong> is automatically calculated based on weights. Click a score to view history.
                </p>
              </div>

              {/* Toolbar Actions */}
              <div style={{ padding: '1rem', backgroundColor: '#f5f5f5', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
                <h3 style={{ margin: '0 0 1rem 0', color: '#424242', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  🛠️ Toolbar Actions
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem' }}>
                    <span style={{ padding: '4px 8px', backgroundColor: 'white', color: '#374151', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.8rem' }}>📤 Export</span>
                    <span style={{ color: '#555' }}>Download as Excel</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem' }}>
                    <span style={{ padding: '4px 8px', backgroundColor: 'white', color: '#374151', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.8rem' }}>📥 Import</span>
                    <span style={{ color: '#555' }}>Upload Excel file</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem' }}>
                    <span style={{ padding: '4px 8px', backgroundColor: 'white', color: '#374151', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.8rem' }}>⚙️ Columns</span>
                    <span style={{ color: '#555' }}>Show/hide columns</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem' }}>
                    <span style={{ padding: '4px 8px', backgroundColor: 'white', color: '#374151', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.8rem' }}>⚖️ Criteria</span>
                    <span style={{ color: '#555' }}>Set score weights</span>
                  </div>
                </div>
              </div>
              </HelpWithVideoTour>
            </div>

            {/* Footer */}
            <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid #e0e0e0', textAlign: 'right', backgroundColor: '#fafafa', borderRadius: '0 0 12px 12px' }}>
              <button 
                onClick={() => setShowHelpModal(false)} 
                style={{ 
                  padding: '0.6rem 1.5rem', 
                  backgroundColor: '#17a2b8', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '6px', 
                  cursor: 'pointer', 
                  fontSize: '1rem',
                  fontWeight: '500'
                }}
              >
                Got it!
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SelectedContractors;

